/**
 * 2D Station Layout Planer mit Zoom und Pan
 * Verbesserte Version mit sauberer Code-Struktur
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { notify, confirmDialog } from '../ui/notify';
import StationBuildGuide from './StationBuildGuide';

const StationPlanner2D = ({ 
  station = 'G1', 
  initialElements = [], 
  onElementsChange,
  onSave,
  isLoading = false,
  user = null
}) => {
  const canvasRef = useRef(null);
  const [elements, setElements] = useState(initialElements);
  const [selectedElement, setSelectedElement] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPreview, setDragPreview] = useState(null); // Für smooth dragging ohne state updates
  const dragTimeoutRef = useRef(null); // Für throttling der drag updates
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [view3D, setView3D] = useState(false);
  const [gridSize] = useState(5); // 5px = 0,25m (feineres Grid: 4 Unterboxen pro 1x1m)
  const [currentStation, setCurrentStation] = useState(station); // Track current station
  const [buildGuideData, setBuildGuideData] = useState(null); // Track build guide data
  const [isLoadingStation, setIsLoadingStation] = useState(false); // NEUE Loading-Sperre
  const [showGrid, setShowGrid] = useState(true);
  const [showMeasurements, setShowMeasurements] = useState(true);
  const [showIcons, setShowIcons] = useState(true);
  const [showHitAreas, setShowHitAreas] = useState(false); // Debug: Hit-Areas anzeigen
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Zoom und Pan State
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [canPan, setCanPan] = useState(false); // Für Cursor-Feedback

  // Maßband-Tool State
  const [measureMode, setMeasureMode] = useState(false);
  const [measureStart, setMeasureStart] = useState(null);
  const [measureEnd, setMeasureEnd] = useState(null);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurements, setMeasurements] = useState([]); // Gespeicherte Messungen

  // Bauzaun-System State
  const [fenceSystemMode, setFenceSystemMode] = useState(false);
  const [isDefiningSystem, setIsDefiningSystem] = useState(false);
  const [currentSystemFences, setCurrentSystemFences] = useState([]); // Aktuell ausgewählte Bauzäune
  const [fenceSystems, setFenceSystems] = useState([]); // Alle definierten Systeme
  const [showSystemNumberInput, setShowSystemNumberInput] = useState(false);
  const [systemNumberInput, setSystemNumberInput] = useState('');
  const [showDoubleFenceSideDialog, setShowDoubleFenceSideDialog] = useState(false);
  const [pendingDoubleFence, setPendingDoubleFence] = useState(null); // Für Dialog
  
  // System-Management State
  const [selectedSystemId, setSelectedSystemId] = useState(null); // Für Highlighting
  const [editingSystemId, setEditingSystemId] = useState(null); // Für Bearbeitung

  // Undo/Redo System State
  const [undoHistory, setUndoHistory] = useState([]); // Letzte 3 States
  const [currentActionDescription, setCurrentActionDescription] = useState(''); // Beschreibung der aktuellen Aktion

  // Custom Elements State
  const [customElements, setCustomElements] = useState([]);
  const [showCustomElementForm, setShowCustomElementForm] = useState(false);
  
  // Element Sichtbarkeits-State
  const [elementVisibility, setElementVisibility] = useState({
    // Alle Bauzaun-Typen (Standard: sichtbar)
    fence_normal: true,
    fence_wood_single: true,
    fence_wood_double: true,
    fence_with_tarp: true,
    fence_foot: true,
    // Zelte
    pagoda_4x4: true,
    pagoda_4x6: true,
    partyzelt: true,
    // Container
    container_20ft: true,
    container_40ft: true,
    // Möbel
    regal: true,
    biertisch: true,
    // Custom Elements werden dynamisch hinzugefügt
  });
  
  // Debug: Log elementVisibility state changes
  useEffect(() => {
    console.log('🔧 ElementVisibility state updated:', elementVisibility);
  }, [elementVisibility]);
  
  // Element-Sichtbarkeit umschalten
  const toggleElementVisibility = (elementType) => {
    console.log('🔄 Toggle visibility for:', elementType);
    console.log('🔄 Current visibility:', elementVisibility[elementType]);
    setElementVisibility(prev => {
      const newState = {
        ...prev,
        [elementType]: !prev[elementType]
      };
      console.log('🔄 New visibility state:', newState);
      return newState;
    });
  };
  
  // Prüfen ob ein Element-Typ sichtbar ist
  const isElementTypeVisible = (elementType) => {
    if (!elementType) return true; // Wenn kein Type definiert, ist es sichtbar
    const visible = elementVisibility[elementType] !== false; // Standard: sichtbar wenn undefined
    console.log('🔍 Element type visibility check:', elementType, 'visible:', visible, 'state value:', elementVisibility[elementType]);
    return visible;
  };
  
  // Prüfen ob ein Element sichtbar ist (berücksichtigt sowohl type als auch elementType)
  const isElementVisible = (element) => {
    console.log('👁️ Checking visibility for element:', element.id, 'type:', element.type, 'elementType:', element.elementType);
    
    // Prioritize elementType over type, as elementType is what we track in visibility state
    const checkType = element.elementType || element.type;
    const visible = isElementTypeVisible(checkType);
    
    console.log('👁️ Using type:', checkType, 'for visibility check, result:', visible);
    return visible;
  };
  
  const [newCustomElement, setNewCustomElement] = useState({
    name: '',
    icon: '🟦',
    width: 20, // in Pixeln (20px = 1m)
    height: 20,
    color: '#4a90e2',
    strokeColor: '#2c5aa0',
    realWidth: 1.0, // in Metern
    realHeight: 1.0,
    realHeightZ: 1.0, // Höhe für 3D-Ansicht
    category: 'umgebung',
    description: ''
  });

  // Statistik-Hilfsfunktionen
  const getElementStats = () => {
    const stats = {
      bauzaun_normal: elements.filter(el => 
        el.type === 'bauzaun' || 
        el.type === 'bauzaun_normal' || 
        el.elementType === 'fence_normal'
      ).length,
      
      bauzaun_holz_einfach: elements.filter(el => 
        el.type === 'bauzaun_holz_einfach' || 
        el.elementType === 'fence_wood_single'
      ).length,
      
      fence_wood_double: elements.filter(el => 
        el.type === 'bauzaun_doppel' || 
        el.type === 'bauzaun_holz_doppel' ||
        el.type === 'fence_wood_double' || 
        el.elementType === 'fence_wood_double'
      ).length,
      
      fence_with_tarp: elements.filter(el => 
        el.type === 'fence_with_tarp' || 
        el.elementType === 'fence_with_tarp'
      ).length,
      
      fence_foot: elements.filter(el => 
        el.type === 'fence_foot' || 
        el.elementType === 'fence_foot'
      ).length,
      
      pagoda_4x4: elements.filter(el => 
        el.type === 'pagoda_4x4' || 
        el.type === 'pagodenzelt_4x4' || 
        el.elementType === 'pagoda_4x4'
      ).length,
      
      pagoda_4x6: elements.filter(el => 
        el.type === 'pagoda_4x6' || 
        el.type === 'pagodenzelt_4x6' || 
        el.elementType === 'pagoda_4x6'
      ).length,
      
      partyzelt: elements.filter(el => 
        el.type === 'partyzelt' || 
        el.elementType === 'partyzelt' || 
        el.elementType === 'party_tent'
      ).length,
      
      container_20ft: elements.filter(el => 
        el.type === 'container_20ft' || 
        el.elementType === 'container_20ft'
      ).length,
      
      container_40ft: elements.filter(el => 
        el.type === 'container_40ft' || 
        el.elementType === 'container_40ft'
      ).length,
      
      regal: elements.filter(el => 
        el.type === 'regal' || 
        el.elementType === 'regal' || 
        el.elementType === 'shelves'
      ).length,
      
      biertisch: elements.filter(el => 
        el.type === 'biertisch' || 
        el.elementType === 'biertisch' || 
        el.elementType === 'beer_table'
      ).length
    };
    
    // Verwende Doppelzaun-Seiten für Gesamtzählung
    stats.fence_wood_double_seiten = stats.fence_wood_double * 2; // Jede Seite zählt
    stats.totalBauzaun = stats.bauzaun_normal + stats.bauzaun_holz_einfach + stats.fence_wood_double_seiten + stats.fence_with_tarp;
    stats.totalZelte = stats.pagoda_4x4 + stats.pagoda_4x6 + stats.partyzelt;
    stats.totalContainer = stats.container_20ft + stats.container_40ft;
    stats.totalMoebel = stats.regal + stats.biertisch;
    stats.totalCustom = elements.filter(el => 
      el.isCustom || 
      el.type === 'custom' || 
      el.elementType === 'custom'
    ).length;
    stats.total = elements.length;
    
    return stats;
  };

  const elementStats = getElementStats();

  // Elements synchronisieren wenn sich initialElements ändern (nur wenn elements leer ist)
  useEffect(() => {
    if (elements.length === 0 && initialElements.length > 0) {
      console.log('🔄 Initializing elements from props:', initialElements);
      setElements(initialElements);
    }
  }, [initialElements]); // elements bewusst NICHT in dependencies um loop zu vermeiden

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [elements]);

  // Station prop mit currentStation synchronisieren und Auto-Save vor Wechsel
  useEffect(() => {
    const handleStationChange = async () => {
      if (station !== currentStation) {
        console.log('🔄 Station changed from', currentStation, 'to', station);
        
        // Build Guide der aktuellen Station vor Wechsel sichern
        if (currentStation && buildGuideData) {
          try {
            console.log('💾 Saving Build Guide before station change:', currentStation);
            const response = await fetch(`/api/station-layouts/${currentStation}/build-guide`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(buildGuideData)
            });
            
            if (response.ok) {
              console.log('✅ Build Guide saved before station change');
            } else {
              console.warn('⚠️ Build Guide save before station change failed');
            }
          } catch (error) {
            console.error('❌ Error saving Build Guide before station change:', error);
          }
        }
        
        // WICHTIG: Auto-Save NUR mit den Daten der aktuellen Station, NICHT mit den neuen!
        // Die Elements/FenceSystems könnten bereits von der neuen Station sein!
        if (currentStation) {
          console.log('� DEAKTIVIERT: Auto-save vor Stationswechsel um Datenverlust zu verhindern');
          // Das Auto-Save vor Stationswechsel ist deaktiviert, weil es zu Datenverlusten führt
          // Stattdessen sollte manuell gespeichert werden oder das normale Auto-Save System verwendet werden
        }
        
        // Build Guide Data zurücksetzen für neue Station
        setBuildGuideData(null);
        
        // Sofort die neue Station setzen
        setCurrentStation(station);
      }
    };
    
    handleStationChange();
  }, [station, currentStation, buildGuideData]);  // buildGuideData zu Dependencies hinzugefügt

  // Station Layout vom Server laden wenn Station wechselt (MIT LOADING-SPERRE)
  useEffect(() => {
    const loadStationElements = async () => {
      if (!station) return;
      
      // WICHTIG: Loading-Sperre setzen um Auto-Save zu verhindern
      setIsLoadingStation(true);
      
      console.log('🔍 Loading station elements for:', station);
      
      try {
        const response = await fetch(`/api/station-layouts/${station}`);
        console.log('📡 Station API response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('📦 Station data received:', data);
          console.log('📦 Raw fence systems from server:', data.fenceSystems);
          console.log('📦 Fence systems type:', typeof data.fenceSystems);
          console.log('📦 Fence systems length:', Array.isArray(data.fenceSystems) ? data.fenceSystems.length : 'not array');
          
          const layoutData = data.elements || [];
          const systemsData = data.fenceSystems || [];
          const visibilityData = data.elementVisibility || {};
          console.log('🎯 Parsed layout data:', layoutData);
          console.log('🚧 Parsed fence systems:', systemsData);
          console.log('👁️ Parsed visibility settings:', visibilityData);
          
          console.log(`📥 Setting elements from server for station ${station}: ${layoutData.length} elements, ${systemsData.length} fence systems`);
          
          // Erst currentStation synchronisieren, dann Elements setzen
          setCurrentStation(station);
          setElements(layoutData);
          setFenceSystems(systemsData);
          
          // Element-Sichtbarkeit laden und mit Standard-Werten ergänzen
          setElementVisibility(prev => ({
            ...prev, // Standard-Sichtbarkeit beibehalten
            ...visibilityData // Gespeicherte Einstellungen überschreiben
          }));
          
          setHasUnsavedChanges(false); // Frisch geladene Daten sind gespeichert
        } else {
          console.log('📭 New station - empty layout');
          setCurrentStation(station);
          setElements([]);
          setFenceSystems([]);
          setHasUnsavedChanges(false); // Leere Station ist "gespeichert"
        }
      } catch (error) {
        console.error('❌ Fehler beim Laden des Station Layouts:', error);
        setCurrentStation(station);
        setElements([]);
        setFenceSystems([]); // Auch bei Fehler Fence Systems zurücksetzen
        setHasUnsavedChanges(false); // Auch bei Fehler keine "ungespeicherten" Änderungen
      } finally {
        // Loading-Sperre entfernen
        setIsLoadingStation(false);
      }
    };
    
    loadStationElements();
    
    // Station-spezifische States zurücksetzen
    setSelectedSystemId(null);
    setEditingSystemId(null);
    setFenceSystemMode(false);
    setCurrentSystemFences([]);
    setIsDefiningSystem(false);
    setShowSystemNumberInput(false);
    setSystemNumberInput('');
    setShowDoubleFenceSideDialog(false);
    setPendingDoubleFence(null);
    setUndoHistory([]); // Undo-Historie bei Stationswechsel zurücksetzen
  }, [station]); // Lade IMMER wenn sich die Station ändert

  // Custom Elements laden/speichern vom Server
  useEffect(() => {
    const loadCustomElements = async () => {
      try {
        const response = await fetch(`/api/station-layouts/${station}/custom-elements`);
        if (response.ok) {
          const data = await response.json();
          setCustomElements(data.customElements || []);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Custom Elements:', error);
        // Fallback: versuche localStorage
        const savedCustomElements = localStorage.getItem(`customElements_${station}`);
        if (savedCustomElements) {
          try {
            setCustomElements(JSON.parse(savedCustomElements));
          } catch (parseError) {
            console.error('Fehler beim Parsen der localStorage Custom Elements:', parseError);
          }
        }
      }
    };
    
    loadCustomElements();
  }, [station]);

  // Element-Bibliothek basierend auf echten Fotos und Maßen (0,5m Grid: 20px = 1m)
  const elementLibrary = {
    pagoda_4x4: {
      name: '🏕️ Pagodenzelt 4x4m',
      width: 80, // 4m * 20px/m (korrigierter Maßstab)
      height: 80,
      color: '#ffffff',
      strokeColor: '#cccccc',
      type: 'pagoda_4x4',
      icon: '🏕️',
      realSize: '4x4m',
      realHeight: 3.2,
      heightInfo: 'Seitenhöhe 2.2m, Firsthöhe 3.2m (Pagoda-Dach)'
    },
    pagoda_4x6: {
      name: '🏕️ Pagodenzelt 4x6m', 
      width: 80,
      height: 120, // 6m * 20px/m
      color: '#ffffff',
      strokeColor: '#cccccc',
      type: 'pagoda_4x6',
      icon: '🏕️',
      realSize: '4x6m',
      realHeight: 3.5,
      heightInfo: 'Seitenhöhe 2.4m, Firsthöhe 3.5m (Pagoda-Dach)'
    },
    partyzelt: {
      name: '🎪 Partyzelt (7x10m)',
      width: 140, // 7m * 20px/m
      height: 200, // 10m * 20px/m
      color: '#f0f8ff',
      strokeColor: '#4169e1',
      type: 'party_tent',
      icon: '🎪',
      realSize: '7x10m',
      realHeight: 3.5,
      heightInfo: 'Seitenhöhe 2.0m, Firsthöhe 3.5m (Satteldach)'
    },
    container_20ft: {
      name: '📦 Container 20ft',
      width: 120, // 6m * 20px/m
      height: 48, // 2.4m * 20px/m
      color: '#4682B4',
      strokeColor: '#2F4F4F',
      type: 'container_20ft',
      icon: '�',
      realSize: '6x2.4m',
      realHeight: 2.59,
      heightInfo: 'Standard ISO-Container Höhe'
    },
    container_40ft: {
      name: '📦 Container 40ft',
      width: 240, // 12m * 20px/m
      height: 48, // 2.4m * 20px/m
      color: '#4169E1',
      strokeColor: '#2F4F4F',
      type: 'container_40ft',
      icon: '📦',
      realSize: '12x2.4m',
      realHeight: 2.59,
      heightInfo: 'Standard ISO-Container Höhe'
    },
    regal: {
      name: '📚 Metall-Regal',
      width: 24, // 1.2m * 20px/m
      height: 16, // 0.8m * 20px/m  
      color: '#d3d3d3',
      strokeColor: '#555555',
      type: 'shelves',
      icon: '📚',
      realSize: '1.2x0.8m',
      realHeight: 2.0,
      heightInfo: 'Standard Regal-Höhe'
    },
    biertisch: {
      name: '🪑 Biertisch',
      width: 44, // 2.2m * 20px/m (Echte Biertisch-Maße: 220cm breit)
      height: 16, // 0.78m * 20px/m (78cm tief)
      color: '#8B4513',
      strokeColor: '#654321',
      type: 'beer_table',
      icon: '�',
      realSize: '2.2x0.78m',
      realHeight: 0.70,
      heightInfo: 'Standard Biertisch-Höhe (70cm)'
    },
    bauzaun_normal: {
      name: '🚧 Bauzaun Standard',
      width: 70, // 3.5m Länge * 20px/m 
      height: 2, // 2cm Breite (minimal dargestellt als dünne Linie)
      color: '#ffc107',
      strokeColor: '#ff8f00',
      type: 'fence_normal',
      icon: '🚧',
      realSize: '3.5m lang × 2cm breit × 2m hoch',
      realHeight: 2.0,
      heightInfo: 'Standard Bauzaun-Höhe: 2m'
    },
    bauzaun_holz_einfach: {
      name: '🪵 Bauzaun belattet (einfach)',
      width: 70, // 3.5m Länge * 20px/m
      height: 2, // 2cm Breite (minimal dargestellt als dünne Linie)
      color: '#8B4513',
      strokeColor: '#654321',
      type: 'fence_wood_single',
      icon: '🪵',
      realSize: '3.5m lang × 2cm breit × 2m hoch',
      realHeight: 2.0,
      heightInfo: 'Bauzaun mit einseitiger Belattung'
    },
    fence_wood_double: {
      name: '🪵🪵 Bauzaun belattet (doppelt)',
      width: 70, // 3.5m Länge * 20px/m
      height: 2, // 2cm Breite (minimal dargestellt als dünne Linie)
      color: '#654321',
      strokeColor: '#4A4A4A',
      type: 'fence_wood_double',
      icon: '🪵🪵',
      realSize: '3.5m lang × 2cm breit × 2m hoch',
      realHeight: 2.0,
      heightInfo: 'Bauzaun mit beidseitiger Belattung'
    },
    fence_with_tarp: {
      name: '🪖 Bauzaun mit Plane',
      width: 70, // 3.5m Länge * 20px/m
      height: 2, // 2cm Breite (minimal dargestellt als dünne Linie)
      color: '#228B22',
      strokeColor: '#006400',
      type: 'fence_with_tarp',
      icon: '🪖',
      realSize: '3.5m lang × 2cm breit × 2m hoch',
      realHeight: 2.0,
      heightInfo: 'Bauzaun mit Sichtschutzplane'
    },
    fence_foot: {
      name: '🦶 Bauzaunfuß 24kg',
      width: 13,
      height: 5,
      color: '#2C3E50',
      strokeColor: '#1A252F',
      type: 'fence_foot',
      icon: '🦶',
      realSize: '0.66x0.25m',
      realHeight: 0.14,
      heightInfo: 'Betonfuß 14cm hoch'
    }
  };

  // Auto-Save System mit Station-Validierung und Loading-Sperre
  const saveToServer = useCallback(async () => {
    console.log('💾 saveToServer called with:', { 
      station: station,
      currentStation: currentStation,
      isLoadingStation: isLoadingStation,
      elements: elements.length, 
      fenceSystems: fenceSystems.length 
    });
    
    // KRITISCHE SICHERHEITSPRÜFUNGEN
    if (isLoadingStation) {
      console.log('🚫 Save blocked: Station loading in progress');
      return;
    }
    
    if (station !== currentStation) {
      console.log('🚫 Save blocked: Station mismatch!', { station, currentStation });
      return;
    }
    
    if (elements.length === 0 && fenceSystems.length === 0) {
      console.log('🚫 Save skipped: No data to save');
      return;
    }
    
    setIsSaving(true);
    try {
      // Station-Validierung und Warnung bei vielen Elementen
      if (elements.length > 20) {
        console.warn(`⚠️  Speichere große Anzahl Elemente (${elements.length}) für Station ${station}`);
      }
      
      // Verwende onSave prop wenn verfügbar, sonst direkte API
      if (onSave) {
        console.log(`💾 Using onSave prop for station ${station}`);
        await onSave(station, { elements, fenceSystems, elementVisibility });
      } else {
        console.log(`💾 Using direct API call for station ${station}`);
        console.log('💾 Sending data:', { 
          station: station,
          elements: elements.length + ' elements', 
          fenceSystems: fenceSystems.length + ' systems' 
        });
        
        // Direkte Station Layout API verwenden
        const response = await fetch(`/api/station-layouts/${station}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ elements, fenceSystems, elementVisibility })
        });
        
        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`Fehler beim Speichern für Station ${station}: ${errorData}`);
        }
        
        const result = await response.json();
        console.log(`💾 Save successful for station ${station}:`, result);
      }
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
    } finally {
      setIsSaving(false);
    }
  }, [station, elements, fenceSystems, elementVisibility, onSave]);

  // Auto-Save alle 30 Sekunden (MIT STATION-VALIDIERUNG UND LOADING-SPERRE)
  useEffect(() => {
    if (!onSave) return;
    
    const autoSaveInterval = setInterval(() => {
      // MEHRFACH-SICHERHEITSPRÜFUNG für Auto-Save
      const canAutoSave = (elements.length > 0 || fenceSystems.length > 0) && 
                         station === currentStation && 
                         !isLoadingStation;
      
      if (canAutoSave) {
        console.log(`💾 Auto-save für Station ${station} (Elements: ${elements.length}, FenceSystems: ${fenceSystems.length})`);
        saveToServer();
      } else {
        if (station !== currentStation) {
          console.log(`🚫 Auto-save gestoppt: Station-Mismatch (station: ${station}, currentStation: ${currentStation})`);
        } else if (isLoadingStation) {
          console.log(`🚫 Auto-save gestoppt: Loading in progress für Station ${station}`);
        }
      }
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [saveToServer, elements, fenceSystems, station, currentStation, isLoadingStation]);  // isLoadingStation hinzugefügt

  // Undo/Redo System
  const saveToHistory = useCallback((description) => {
    const newSnapshot = {
      elements: JSON.parse(JSON.stringify(elements)),
      fenceSystems: JSON.parse(JSON.stringify(fenceSystems)),
      timestamp: new Date(),
      description: description
    };
    
    setUndoHistory(prev => {
      const newHistory = [newSnapshot, ...prev];
      return newHistory.slice(0, 3); // Nur die letzten 3 behalten
    });
  }, [elements, fenceSystems]);

  const undoLastAction = useCallback((historyIndex) => {
    if (historyIndex < undoHistory.length) {
      const targetSnapshot = undoHistory[historyIndex];
      
      // State wiederherstellen
      setElements(targetSnapshot.elements);
      setFenceSystems(targetSnapshot.fenceSystems);
      
      // History bis zu diesem Punkt entfernen
      setUndoHistory(prev => prev.slice(historyIndex + 1));
      
      // Als Änderung markieren
      setHasUnsavedChanges(true);
      
      console.log(`🔄 Undo: ${targetSnapshot.description}`);
    }
  }, [undoHistory]);

  // Zoom-Funktionen
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  // Build Guide Data Change Handler
  const handleBuildGuideDataChange = useCallback((guideData) => {
    // Nur updates verarbeiten, wenn es die aktuelle Station ist
    if (guideData.stationId === currentStation) {
      setBuildGuideData(guideData);
      console.log('📋 Build Guide data updated locally for station:', guideData.stationId);
    }
  }, [currentStation]);

  // Build Guide Handler mit Schutz vor Station-Wechsel
  const handleBuildGuideUpdate = async (stationId, guideData) => {
    // WICHTIG: Nur speichern wenn es die aktuelle Station ist!
    if (stationId !== currentStation) {
      console.log('🚫 Build Guide save blocked: Station mismatch!', { 
        receivedStationId: stationId, 
        currentStation 
      });
      return;
    }
    
    console.log('📋 Build Guide updating for station:', currentStation, guideData);
    
    try {
      // Build Guide Daten lokal cachen
      setBuildGuideData({ ...guideData, stationId });
      
      // An Backend speichern
      const response = await fetch(`/api/station-layouts/${stationId}/build-guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(guideData)
      });

      if (!response.ok) {
        throw new Error('Fehler beim Speichern der Aufbauanleitung');
      }
      
      console.log('✅ Build Guide saved successfully for station:', stationId);
    } catch (error) {
      console.error('❌ Fehler beim Speichern der Build Guide:', error);
      throw error; // Weiterwerfen für Fehlerbehandlung in der Komponente
    }
  };

  // Mausrad-Zoom
  // Zoom-Funktionen mit useCallback
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoomLevel = Math.max(0.1, Math.min(5, zoomLevel * delta));
    
    if (newZoomLevel !== zoomLevel) {
      const zoomPoint = {
        x: (mouseX - panOffset.x) / zoomLevel,
        y: (mouseY - panOffset.y) / zoomLevel
      };
      
      const newPanOffset = {
        x: mouseX - zoomPoint.x * newZoomLevel,
        y: mouseY - zoomPoint.y * newZoomLevel
      };
      
      setZoomLevel(newZoomLevel);
      setPanOffset(newPanOffset);
    }
  }, [zoomLevel, panOffset]);

  // Synchronisiere selectedElement nur wenn nicht gedraggt wird
  useEffect(() => {
    // Keine Synchronisation während Dragging um Blinken zu vermeiden
    if (isDragging) return;
    
    if (selectedElement) {
      const updatedElement = elements.find(el => el.id === selectedElement.id);
      if (updatedElement && 
          (Math.abs(updatedElement.x - (selectedElement.x || 0)) > 5 || 
           Math.abs(updatedElement.y - (selectedElement.y || 0)) > 5)) {
        setSelectedElement(updatedElement);
      }
    }
  }, [elements, isDragging]); // Nur bei nicht-dragging Updates

  // Canvas Setup (nur einmal beim Mount)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = 900;
    canvas.height = 700;
    
    // Nicht-passiver Wheel-Event-Listener für preventDefault
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]); // drawCanvas entfernt um infinite loop zu vermeiden

  // Raster zeichnen - feineres Grid mit 0,25m und 1m Linien
  const drawGrid = useCallback((ctx, width, height) => {
    const step = gridSize; // 5px = 0,25m
    const startX = Math.floor(-panOffset.x / zoomLevel / step) * step;
    const startY = Math.floor(-panOffset.y / zoomLevel / step) * step;
    
    // Feine Hilfslinien (alle 0,25m) - sehr dünn
    ctx.strokeStyle = '#f8f8f8';
    ctx.lineWidth = 0.3 / zoomLevel;
    
    for (let x = startX; x <= width - panOffset.x / zoomLevel; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, -panOffset.y / zoomLevel);
      ctx.lineTo(x, height - panOffset.y / zoomLevel);
      ctx.stroke();
    }
    
    for (let y = startY; y <= height - panOffset.y / zoomLevel; y += step) {
      ctx.beginPath();
      ctx.moveTo(-panOffset.x / zoomLevel, y);
      ctx.lineTo(width - panOffset.x / zoomLevel, y);
      ctx.stroke();
    }
    
    // Meter-Linien (alle 1m = 4 * step) - dicker
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 0.8 / zoomLevel;
    const meterStep = step * 4; // 20px = 1m
    
    const meterStartX = Math.floor(-panOffset.x / zoomLevel / meterStep) * meterStep;
    const meterStartY = Math.floor(-panOffset.y / zoomLevel / meterStep) * meterStep;
    
    for (let x = meterStartX; x <= width - panOffset.x / zoomLevel; x += meterStep) {
      ctx.beginPath();
      ctx.moveTo(x, -panOffset.y / zoomLevel);
      ctx.lineTo(x, height - panOffset.y / zoomLevel);
      ctx.stroke();
    }
    
    for (let y = meterStartY; y <= height - panOffset.y / zoomLevel; y += meterStep) {
      ctx.beginPath();
      ctx.moveTo(-panOffset.x / zoomLevel, y);
      ctx.lineTo(width - panOffset.x / zoomLevel, y);
      ctx.stroke();
    }
    
    // Hauptlinien (alle 5m) - dicker und dunkler
    ctx.strokeStyle = '#d0d0d0';
    ctx.lineWidth = 1.2 / zoomLevel;
    const majorStep = step * 20; // Alle 5 Meter (20 * 0,25m = 5m)
    
    const majorStartX = Math.floor(-panOffset.x / zoomLevel / majorStep) * majorStep;
    const majorStartY = Math.floor(-panOffset.y / zoomLevel / majorStep) * majorStep;
    
    for (let x = majorStartX; x <= width - panOffset.x / zoomLevel; x += majorStep) {
      ctx.beginPath();
      ctx.moveTo(x, -panOffset.y / zoomLevel);
      ctx.lineTo(x, height - panOffset.y / zoomLevel);
      ctx.stroke();
    }
    
    for (let y = majorStartY; y <= height - panOffset.y / zoomLevel; y += majorStep) {
      ctx.beginPath();
      ctx.moveTo(-panOffset.x / zoomLevel, y);
      ctx.lineTo(width - panOffset.x / zoomLevel, y);
      ctx.stroke();
    }
    
    // Grid-Koordinaten bei hohem Zoom anzeigen (alle 1m)
    if (zoomLevel > 1.5) {
      ctx.fillStyle = '#999';
      ctx.font = `${8/zoomLevel}px Arial`;
      ctx.textAlign = 'left';
      
      const meterStep = step * 4; // 1m Intervalle
      const coordStartX = Math.floor(-panOffset.x / zoomLevel / meterStep) * meterStep;
      const coordStartY = Math.floor(-panOffset.y / zoomLevel / meterStep) * meterStep;
      
      for (let x = coordStartX; x <= width - panOffset.x / zoomLevel; x += meterStep) {
        for (let y = coordStartY; y <= height - panOffset.y / zoomLevel; y += meterStep) {
          if (x >= 0 && y >= 0) {
            const meterX = Math.round(x / (step * 4)); // Umrechnung zu Metern
            const meterY = Math.round(y / (step * 4));
            ctx.fillText(`${meterX},${meterY}m`, x + 2/zoomLevel, y - 2/zoomLevel);
          }
        }
      }
    }
  }, [gridSize, panOffset, zoomLevel]); // Dependencies für drawGrid

  // Element zeichnen - optimiert mit useCallback
  const drawElement = useCallback((ctx, element, isSelected) => {
    if (!element) return;
    
    console.log('🖌️ Drawing element:', element.type, 'at', element.x, element.y, 'size:', element.width, 'x', element.height, 'isSelected:', isSelected);
    
    if (isSelected) {
      console.log('🔴 Selected element center will be at:', (element.x || 0) + element.width/2, (element.y || 0) + element.height/2);
    }
    
    const { x, y, width, height, rotation, color, strokeColor, type, number, icon } = element;
    
    ctx.save();
    // Übersetze zum Mittelpunkt des Elements für korrekte Rotation
    ctx.translate((x || 0) + width/2, (y || 0) + height/2);
    if (rotation) {
      ctx.rotate((rotation || 0) * Math.PI / 180);
    }
    
    ctx.fillStyle = color || '#cccccc';
    ctx.strokeStyle = isSelected ? '#ff0000' : (strokeColor || '#000000');
    ctx.lineWidth = isSelected ? 3 : 2;
    
    // Zeichne Element vom Mittelpunkt aus
    ctx.fillRect(-width/2, -height/2, width, height);
    ctx.strokeRect(-width/2, -height/2, width, height);
    
    // Holzlatten-Muster für Holzzäune
    if (type === 'fence_wood_single' || type === 'fence_wood_double') {
      ctx.fillStyle = '#654321';
      for (let i = -width/2; i < width/2; i += 8) {
        ctx.fillRect(i, -height/2, 4, height);
      }
    }
    
    // Individuelle Nummerierung für Bauzäune (Rechtsklick)
    if ((type?.includes('fence') || type === 'fence_foot') && element.fenceNumber) {
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.strokeText(element.fenceNumber, 0, 4);
      ctx.fillText(element.fenceNumber, 0, 4);
    }
    
    // Seiten-Nummerierung für doppelte Bauzäune
    if (type === 'fence_wood_double' && (element.sideA || element.sideB)) {
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 12px Arial';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      
      // Berücksichtige Rotation für die Seiten-Orientierung
      const rotation = element.rotation || 0;
      const isHorizontal = element.width > element.height;
      
      // Nach Rotation: bestimme die neue Orientierung
      let effectivelyHorizontal = isHorizontal;
      if (rotation === 90 || rotation === 270) {
        effectivelyHorizontal = !isHorizontal; // 90° Rotation kehrt Orientierung um
      }
      
      if (effectivelyHorizontal) {
        // Effektiv horizontal: Teile entlang der Höhe
        // Seite A (oben)
        if (element.sideA && element.sideA.assigned && element.sideA.systemNumber) {
          ctx.textAlign = 'center';
          const sideAText = `A:${element.sideA.systemNumber}`;
          ctx.strokeText(sideAText, 0, -height/4);
          ctx.fillText(sideAText, 0, -height/4);
        }
        
        // Seite B (unten)
        if (element.sideB && element.sideB.assigned && element.sideB.systemNumber) {
          ctx.textAlign = 'center';
          const sideBText = `B:${element.sideB.systemNumber}`;
          ctx.strokeText(sideBText, 0, height/4);
          ctx.fillText(sideBText, 0, height/4);
        }
      } else {
        // Effektiv vertikal: Teile entlang der Breite
        // Seite A (links)
        if (element.sideA && element.sideA.assigned && element.sideA.systemNumber) {
          ctx.textAlign = 'center';
          const sideAText = `A:${element.sideA.systemNumber}`;
          ctx.strokeText(sideAText, -width/4, 0);
          ctx.fillText(sideAText, -width/4, 0);
        }
        
        // Seite B (rechts)
        if (element.sideB && element.sideB.assigned && element.sideB.systemNumber) {
          ctx.textAlign = 'center';
          const sideBText = `B:${element.sideB.systemNumber}`;
          ctx.strokeText(sideBText, width/4, 0);
          ctx.fillText(sideBText, width/4, 0);
        }
      }
    }
    
    // Legacy: Alte Nummerierung für Bauzäune (falls noch verwendet)
    if ((type === 'fence_normal' || type === 'fence_wood_single' || type === 'fence_wood_double') && number && !element.fenceNumber) {
      ctx.fillStyle = '#000000';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(number, 0, 4);
    }
    
    // Icon anzeigen (nur wenn showIcons aktiviert ist)
    if (icon && showIcons && zoomLevel > 0.5) {
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#000000';
      ctx.fillText(icon, 0, -height/4);
    }
    
    // Maßangaben anzeigen bei Auswahl oder größerem Zoom
    if (showMeasurements && (isSelected || zoomLevel > 1.5)) {
      const elementDef = Object.values(elementLibrary).find(def => def.type === element.elementType) || 
                        customElements.find(def => def.type === element.elementType);
      if (elementDef && elementDef.realSize) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 1;
        
        // Maß-Label Hintergrund
        const labelWidth = ctx.measureText(elementDef.realSize).width + 8;
        const labelHeight = 16;
        const labelX = -labelWidth / 2;
        const labelY = height / 2 + 8;
        
        ctx.fillRect(labelX, labelY, labelWidth, labelHeight);
        ctx.strokeRect(labelX, labelY, labelWidth, labelHeight);
        
        // Maß-Text
        ctx.fillStyle = '#000';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(elementDef.realSize, 0, labelY + 12);
      }
    }
    
    ctx.restore();
  }, [showMeasurements, showIcons, zoomLevel]); // Dependencies für drawElement

  // Maßband und Messungen zeichnen
  const drawMeasurements = useCallback((ctx) => {
    // Gespeicherte Messungen zeichnen
    measurements.forEach(measurement => {
      const startX = measurement.start.x * zoomLevel + panOffset.x;
      const startY = measurement.start.y * zoomLevel + panOffset.y;
      const endX = measurement.end.x * zoomLevel + panOffset.x;
      const endY = measurement.end.y * zoomLevel + panOffset.y;
      
      // Messlinie
      ctx.strokeStyle = '#ff6b35';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Endpunkte
      ctx.fillStyle = '#ff6b35';
      ctx.beginPath();
      ctx.arc(startX, startY, 4, 0, 2 * Math.PI);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(endX, endY, 4, 0, 2 * Math.PI);
      ctx.fill();
      
      // Distanz-Label
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2;
      const labelText = `${measurement.distance}m`;
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.strokeStyle = '#ff6b35';
      ctx.lineWidth = 1;
      
      const labelWidth = ctx.measureText(labelText).width + 8;
      const labelHeight = 16;
      const labelX = midX - labelWidth / 2;
      const labelY = midY - 8;
      
      ctx.fillRect(labelX, labelY, labelWidth, labelHeight);
      ctx.strokeRect(labelX, labelY, labelWidth, labelHeight);
      
      ctx.fillStyle = '#333';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(labelText, midX, midY + 4);
    });
    
    // Aktuelle Messung während des Messens
    if (measureMode && isMeasuring && measureStart && measureEnd) {
      const startX = measureStart.x * zoomLevel + panOffset.x;
      const startY = measureStart.y * zoomLevel + panOffset.y;
      const endX = measureEnd.x * zoomLevel + panOffset.x;
      const endY = measureEnd.y * zoomLevel + panOffset.y;
      
      // Live-Messlinie
      ctx.strokeStyle = '#35a7ff';
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Live-Endpunkte
      ctx.fillStyle = '#35a7ff';
      ctx.beginPath();
      ctx.arc(startX, startY, 3, 0, 2 * Math.PI);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(endX, endY, 3, 0, 2 * Math.PI);
      ctx.fill();
      
      // Live-Distanz
      const distance = Math.sqrt(Math.pow(measureEnd.x - measureStart.x, 2) + Math.pow(measureEnd.y - measureStart.y, 2));
      const realDistance = (distance / 20).toFixed(2);
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2;
      
      ctx.fillStyle = 'rgba(53, 167, 255, 0.9)';
      ctx.strokeStyle = '#35a7ff';
      const labelText = `${realDistance}m`;
      const labelWidth = ctx.measureText(labelText).width + 8;
      const labelHeight = 16;
      
      ctx.fillRect(midX - labelWidth/2, midY - 8, labelWidth, labelHeight);
      ctx.strokeRect(midX - labelWidth/2, midY - 8, labelWidth, labelHeight);
      
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(labelText, midX, midY + 4);
    }
  }, [measurements, measureMode, isMeasuring, measureStart, measureEnd, zoomLevel, panOffset]); // Dependencies für drawMeasurements

  // Hilfsfunktion für erweiterte Hit-Detection (vor useCallback definiert!)
  const getHitArea = useCallback((element) => {
    let hitWidth = element.width / 2;
    let hitHeight = element.height / 2;
    
    const minHitSize = 15; // Mindestens 15px Hit-Area
    
    // Spezielle Behandlung für Bauzäune und andere dünne Elemente
    if (element.type?.includes('fence') || element.type === 'fence_foot' || element.type?.includes('bauzaun')) {
      hitWidth = Math.max(hitWidth, minHitSize);
      hitHeight = Math.max(hitHeight, minHitSize);
    } else if (element.width < 20 || element.height < 20) {
      // Für alle anderen sehr kleinen Elemente
      hitWidth = Math.max(hitWidth, minHitSize);
      hitHeight = Math.max(hitHeight, minHitSize);
    }
    
    return { hitWidth, hitHeight };
  }, []);

  // Hilfsfunktion um zu prüfen ob ein Punkt in einem Element liegt
  const isPointInElement = useCallback((x, y, element) => {
    // Element-Zentrum berechnen (wie Element gezeichnet wird)
    const centerX = element.x + element.width/2;
    const centerY = element.y + element.height/2;
    
    // Rotation berücksichtigen
    const rotation = element.rotation || 0;
    
    if (rotation !== 0) {
      // Punkt um das Element-Zentrum rotieren (inverse Rotation)
      const rad = -rotation * Math.PI / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      
      // Punkt relativ zum Zentrum
      const dx = x - centerX;
      const dy = y - centerY;
      
      // Rotiere den Punkt zurück
      const rotatedX = dx * cos - dy * sin;
      const rotatedY = dx * sin + dy * cos;
      
      // Prüfe gegen unrotierte Hit-Area
      const { hitWidth, hitHeight } = getHitArea(element);
      const isHit = Math.abs(rotatedX) <= hitWidth && Math.abs(rotatedY) <= hitHeight;
      
      // Debug-Ausgabe für rotierte Bauzaun-Elemente
      if (element.type?.includes('fence') || element.type === 'fence_foot' || element.type?.includes('bauzaun')) {
        console.log(`Hit test for rotated ${element.type} (ID: ${element.id}, Rot: ${rotation}°):`, {
          clickPos: { x, y },
          elementCenter: { x: centerX, y: centerY },
          rotatedPos: { x: rotatedX, y: rotatedY },
          hitArea: { hitWidth, hitHeight },
          isHit
        });
      }
      
      return isHit;
    } else {
      // Normale Hit-Detection ohne Rotation
      const dx = x - centerX;
      const dy = y - centerY;
      const { hitWidth, hitHeight } = getHitArea(element);
      const isHit = Math.abs(dx) <= hitWidth && Math.abs(dy) <= hitHeight;
      
      // Debug-Ausgabe für Bauzaun-Elemente
      if (element.type?.includes('fence') || element.type === 'fence_foot' || element.type?.includes('bauzaun')) {
        console.log(`Hit test for ${element.type} (ID: ${element.id}):`, {
          clickPos: { x, y },
          elementPos: { x: element.x, y: element.y },
          elementCenter: { x: centerX, y: centerY },
          distance: { dx, dy },
          hitArea: { hitWidth, hitHeight },
          isHit
        });
      }
      
      return isHit;
    }
  }, [getHitArea]);

  // Spezielle Hit-Detection für doppelte Bauzäune (unterscheidet zwischen Seiten)
  const getDoubleFenceSide = useCallback((x, y, element) => {
    if (element.elementType !== 'fence_wood_double') return null;
    
    const centerX = element.x + element.width/2;
    const centerY = element.y + element.height/2;
    const rotation = element.rotation || 0;
    
    let dx = x - centerX;
    let dy = y - centerY;
    
    // Rotation berücksichtigen
    if (rotation !== 0) {
      // Punkt um das Element-Zentrum rotieren (inverse Rotation)
      const rad = -rotation * Math.PI / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      
      // Rotiere den Punkt zurück zur ursprünglichen Orientierung
      const rotatedX = dx * cos - dy * sin;
      const rotatedY = dx * sin + dy * cos;
      
      dx = rotatedX;
      dy = rotatedY;
    }
    
    // Bestimme ursprüngliche Orientierung
    const isOriginallyHorizontal = element.width > element.height;
    
    // Nach Rotation: bestimme die neue effektive Orientierung
    let effectivelyHorizontal = isOriginallyHorizontal;
    if (rotation === 90 || rotation === 270) {
      effectivelyHorizontal = !isOriginallyHorizontal;
    }
    
    if (effectivelyHorizontal) {
      // Effektiv horizontal: Teile entlang der Höhe (Y-Achse)
      return dy < 0 ? 'sideA' : 'sideB'; // Oben (A) vs Unten (B)
    } else {
      // Effektiv vertikal: Teile entlang der Breite (X-Achse)  
      return dx < 0 ? 'sideA' : 'sideB'; // Links (A) vs Rechts (B)
    }
  }, []);

  // Bauzaun-System visuelles Feedback zeichnen
  const drawFenceSystemFeedback = useCallback((ctx) => {
    // Highlight für ausgewähltes System aus der Sidebar
    if (selectedSystemId !== null && fenceSystems[selectedSystemId]) {
      const selectedSystem = fenceSystems[selectedSystemId];
      selectedSystem.fences.forEach((fence, index) => {
        // Nur sichtbare Bauzäune highlighten
        if (!isElementVisible(fence)) {
          return; // Zaun überspringen wenn nicht sichtbar
        }
        
        // Element-Zentrum verwenden (wie in drawElement)
        const centerX = (fence.x + fence.width/2) * zoomLevel + panOffset.x;
        const centerY = (fence.y + fence.height/2) * zoomLevel + panOffset.y;
        const width = fence.width * zoomLevel;
        const height = fence.height * zoomLevel;
        
        // Highlight-Rahmen für System aus Sidebar (gelb/orange)
        ctx.strokeStyle = '#ff6b35';
        ctx.lineWidth = 4;
        ctx.setLineDash([8, 4]);
        ctx.strokeRect(centerX - width/2 - 8, centerY - height/2 - 8, width + 16, height + 16);
        ctx.setLineDash([]);
        
        // System-Nummer prominent anzeigen
        ctx.fillStyle = '#ff6b35';
        ctx.beginPath();
        ctx.arc(centerX + width/2 + 15, centerY - height/2 - 15, 15, 0, 2 * Math.PI);
        ctx.fill();
        
        ctx.fillStyle = 'white';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(selectedSystem.number, centerX + width/2 + 15, centerY - height/2 - 10);
      });
    }
    
    // Highlight für aktuell ausgewählte Bauzäune während System-Definition
    if (fenceSystemMode && currentSystemFences.length > 0) {
      currentSystemFences.forEach((fence, index) => {
        // Nur sichtbare Bauzäune highlighten
        if (!isElementVisible(fence)) {
          return; // Zaun überspringen wenn nicht sichtbar
        }
        
        // Element-Zentrum verwenden (wie in drawElement)
        const centerX = (fence.x + fence.width/2) * zoomLevel + panOffset.x;
        const centerY = (fence.y + fence.height/2) * zoomLevel + panOffset.y;
        const width = fence.width * zoomLevel;
        const height = fence.height * zoomLevel;
        
        // Highlight-Rahmen um ausgewählte Bauzäune (zentriert)
        ctx.strokeStyle = '#17a2b8';
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 5]);
        
        // Für doppelte Bauzäune nur die gewählte Seite highlighten
        if (fence.elementType === 'fence_wood_double' && fence.selectedSide) {
          const isHorizontal = fence.width > fence.height;
          if (isHorizontal) {
            // Horizontal: Teile entlang der Höhe (schmale Seite)
            const halfHeight = height / 2;
            if (fence.selectedSide === 'sideA') {
              // Obere Hälfte (Seite A)
              ctx.strokeRect(centerX - width/2 - 5, centerY - height/2 - 5, width + 10, halfHeight + 5);
            } else {
              // Untere Hälfte (Seite B)  
              ctx.strokeRect(centerX - width/2 - 5, centerY - 5, width + 10, halfHeight + 10);
            }
          } else {
            // Vertikal: Teile entlang der Breite (schmale Seite)
            const halfWidth = width / 2;
            if (fence.selectedSide === 'sideA') {
              // Linke Hälfte (Seite A)
              ctx.strokeRect(centerX - width/2 - 5, centerY - height/2 - 5, halfWidth + 5, height + 10);
            } else {
              // Rechte Hälfte (Seite B)
              ctx.strokeRect(centerX - 5, centerY - height/2 - 5, halfWidth + 10, height + 10);
            }
          }
        } else {
          // Normale Bauzäune: ganzes Element highlighten
          ctx.strokeRect(centerX - width/2 - 5, centerY - height/2 - 5, width + 10, height + 10);
        }
        
        ctx.setLineDash([]);
        
        // Nummerierung der Reihenfolge (zentriert positioniert)
        ctx.fillStyle = '#17a2b8';
        ctx.beginPath();
        ctx.arc(centerX + width/2 + 10, centerY - height/2 - 10, 12, 0, 2 * Math.PI);
        ctx.fill();
        
        ctx.fillStyle = 'white';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        
        // Seitenkennzeichnung für doppelte Bauzäune
        const displayText = fence.selectedSide ? `${index + 1}${fence.selectedSide.slice(-1)}` : (index + 1).toString();
        ctx.fillText(displayText, centerX + width/2 + 10, centerY - height/2 - 6);
      });
      
      // Verbindungslinien zwischen ausgewählten Bauzäunen
      if (currentSystemFences.length > 1) {
        ctx.strokeStyle = '#17a2b8';
        ctx.lineWidth = 2;
        ctx.setLineDash([10, 5]);
        ctx.beginPath();
        
        for (let i = 0; i < currentSystemFences.length - 1; i++) {
          const fence1 = currentSystemFences[i];
          const fence2 = currentSystemFences[i + 1];
          
          // Element-Zentren für Verbindungslinien verwenden
          const x1 = (fence1.x + fence1.width/2) * zoomLevel + panOffset.x;
          const y1 = (fence1.y + fence1.height/2) * zoomLevel + panOffset.y;
          const x2 = (fence2.x + fence2.width/2) * zoomLevel + panOffset.x;
          const y2 = (fence2.y + fence2.height/2) * zoomLevel + panOffset.y;
          
          if (i === 0) ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
        }
        
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
    
    // Zeichne System-Nummern für alle erstellten Systeme (nur wenn keine individuelle Nummer vorhanden)
    fenceSystems.forEach(system => {
      system.fences.forEach(fence => {
        // Nur System-Nummer anzeigen wenn sichtbar und keine individuelle Nummer gesetzt ist
        if (!isElementVisible(fence)) {
          return; // Zaun überspringen wenn nicht sichtbar
        }
        
        // Nur System-Nummer anzeigen wenn keine individuelle Nummer gesetzt ist
        if (!fence.fenceNumber) {
          // Element-Zentrum verwenden (wie in drawElement)
          const centerX = (fence.x + fence.width/2) * zoomLevel + panOffset.x;
          const centerY = (fence.y + fence.height/2) * zoomLevel + panOffset.y;
          const width = fence.width * zoomLevel;
          const height = fence.height * zoomLevel;
          
          // System-Nummer Label
          const labelText = system.number;
          ctx.font = 'bold 14px Arial';
          const labelWidth = ctx.measureText(labelText).width + 8;
          const labelHeight = 18;
          const labelX = centerX - labelWidth / 2;
          const labelY = centerY - height / 2 - 25;
          
          // Label Hintergrund
          ctx.fillStyle = 'rgba(23, 162, 184, 0.9)';
          ctx.strokeStyle = '#17a2b8';
          ctx.lineWidth = 1;
          ctx.fillRect(labelX, labelY, labelWidth, labelHeight);
          ctx.strokeRect(labelX, labelY, labelWidth, labelHeight);
          
          // Label Text (zentriert)
          ctx.fillStyle = 'white';
          ctx.textAlign = 'center';
          ctx.fillText(labelText, centerX, labelY + 13);
        }
      });
    });
  }, [fenceSystemMode, currentSystemFences, fenceSystems, selectedSystemId, zoomLevel, panOffset]); // Dependencies für drawFenceSystemFeedback

  // Stabile Referenzen für useEffect Dependencies
  const stableMeasureStart = useMemo(() => measureStart, [measureStart?.x, measureStart?.y]);
  const stableMeasureEnd = useMemo(() => measureEnd, [measureEnd?.x, measureEnd?.y]);
  const stableDragPreview = useMemo(() => dragPreview, [dragPreview?.id, dragPreview?.x, dragPreview?.y]);
  const stableSelectedElement = useMemo(() => selectedElement, [selectedElement?.id]);

  // Canvas zeichnen - nach allen Funktionsdefinitionen
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    console.log('🎨 Drawing canvas, elements count:', elements.length);
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    ctx.save();
    ctx.translate(panOffset.x, panOffset.y);
    ctx.scale(zoomLevel, zoomLevel);
    
    if (showGrid) {
      drawGrid(ctx, canvas.width / zoomLevel, canvas.height / zoomLevel);
    }
    
    // Sortiere Elemente nach Z-Order (Hintergrund → Vordergrund)
    const sortedElements = [...elements].sort((a, b) => {
      const getZIndex = (element) => {
        // Z-Order: Zelte ganz im Hintergrund, dann Container, Bauzäune, Möbel
        if (element.type?.includes('zelt') || element.type === 'partyzelt') return 1; // Zelte ganz im Hintergrund
        if (element.type?.includes('container')) return 2; // Container darüber
        if (element.type?.includes('fence') || element.type === 'fence_foot') return 3; // Bauzäune darüber
        if (element.type?.includes('regal') || element.type?.includes('table') || element.type === 'biertisch') return 4; // Möbel im Vordergrund
        return 3; // Default für Custom Elements (mittlere Ebene)
      };
      return getZIndex(a) - getZIndex(b);
    });
    
    console.log('📊 Total elements to render:', sortedElements.length);
    if (sortedElements.length > 0) {
      console.log('📊 First element example:', sortedElements[0]);
      console.log('📊 Element types present:', [...new Set(sortedElements.map(el => el.type || el.elementType))]);
    }
    
    sortedElements.forEach(element => {
      // Nur sichtbare Elemente rendern
      console.log('🎨 Checking element for rendering:', element.id, element.type, element.elementType);
      const visible = isElementVisible(element);
      console.log('🎨 Element visible:', visible);
      if (!visible) {
        console.log('🎨 Skipping element:', element.id);
        return; // Element überspringen wenn nicht sichtbar
      }
      
      console.log('🎨 Rendering element:', element.id);
      // Verwende dragPreview Position für gerade bewegtes Element
      let drawElement_element = element;
      if (dragPreview && element.id === dragPreview.id) {
        drawElement_element = { ...element, x: dragPreview.x, y: dragPreview.y };
      }
      drawElement(ctx, drawElement_element, element.id === selectedElement?.id);
    });
    
    ctx.restore();
    
    // Maßstabs-Anzeige (immer in der unteren rechten Ecke, unabhängig von Zoom/Pan)
    if (showMeasurements) {
      // Maßstab: 20px = 1m (4 * 5px Einheiten = 1m) bei Zoom 1x
      const pixelsPerMeter = 20 * zoomLevel; // 1m = 4 * gridSize
      
      // Verschiedene Maßstabs-Längen je nach Zoom
      let meterLength, displayText;
      if (pixelsPerMeter >= 100) {
        meterLength = 1;
        displayText = "1m";
      } else if (pixelsPerMeter >= 40) {
        meterLength = 2;
        displayText = "2m";
      } else {
        meterLength = 5;
        displayText = "5m";
      }
      
      const scalePixelLength = meterLength * pixelsPerMeter;
      const scaleX = canvas.width - 150;
      const scaleY = canvas.height - 40;
      
      // Maßstab-Hintergrund
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(scaleX - 10, scaleY - 25, 130, 35);
      ctx.strokeStyle = '#ddd';
      ctx.strokeRect(scaleX - 10, scaleY - 25, 130, 35);
      
      // Maßstabs-Linie
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(scaleX, scaleY);
      ctx.lineTo(scaleX + scalePixelLength, scaleY);
      ctx.stroke();
      
      // Endstriche
      ctx.beginPath();
      ctx.moveTo(scaleX, scaleY - 3);
      ctx.lineTo(scaleX, scaleY + 3);
      ctx.moveTo(scaleX + scalePixelLength, scaleY - 3);
      ctx.lineTo(scaleX + scalePixelLength, scaleY + 3);
      ctx.stroke();
      
      // Text
      ctx.fillStyle = '#333';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(displayText, scaleX + scalePixelLength/2, scaleY - 8);
    }
    
    // Maßband und Messungen zeichnen (nach allem anderen)
    drawMeasurements(ctx);
    
    // Bauzaun-System visuelles Feedback zeichnen
    drawFenceSystemFeedback(ctx);
    
    // Debug: Hit-Areas für alle Elemente anzeigen wenn aktiviert
    if (showHitAreas) {
      elements.forEach(element => {
        // Element-Zentrum berechnen (wie in drawElement)
        const centerX = (element.x + element.width/2) * zoomLevel + panOffset.x;
        const centerY = (element.y + element.height/2) * zoomLevel + panOffset.y;
        const { hitWidth, hitHeight } = getHitArea(element);
        const scaledHitWidth = hitWidth * zoomLevel;
        const scaledHitHeight = hitHeight * zoomLevel;
        
        // Hit-Area visualisieren (zentriert um das Element-Zentrum)
        ctx.strokeStyle = element.type?.includes('fence') || element.type === 'fence_foot' || element.type?.includes('bauzaun')
          ? 'rgba(255, 0, 0, 0.7)' // Rot für Bauzäune
          : 'rgba(0, 255, 0, 0.5)'; // Grün für andere Elemente
        ctx.lineWidth = 2;
        ctx.setLineDash([3, 3]);
        ctx.strokeRect(
          centerX - scaledHitWidth, 
          centerY - scaledHitHeight, 
          scaledHitWidth * 2, 
          scaledHitHeight * 2
        );
        ctx.setLineDash([]);
        
        // Hit-Area Text (zentriert unter dem Element)
        ctx.fillStyle = element.type?.includes('fence') || element.type === 'fence_foot' || element.type?.includes('bauzaun')
          ? 'rgba(255, 0, 0, 0.9)' 
          : 'rgba(0, 255, 0, 0.9)';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(hitWidth)}x${Math.round(hitHeight)}px`, centerX, centerY + scaledHitHeight + 15);
      });
    }
  }, [
    elements,
    stableSelectedElement,
    showGrid,
    showIcons,
    showHitAreas,
    zoomLevel,
    panOffset,
    showMeasurements,
    measureMode,
    isMeasuring,
    stableMeasureStart,
    stableMeasureEnd,
    measurements,
    stableDragPreview,
    drawElement,
    drawGrid,
    drawMeasurements,
    fenceSystemMode,
    isDefiningSystem,
    currentSystemFences,
    fenceSystems,
    getHitArea,
    elementVisibility
  ]);

  // Snap to Grid - verbesserte Version für präzise Positionierung
  const snapToGrid = (value) => {
    // Runde auf nächsten Grid-Punkt (20px = 1m)
    return Math.round(value / gridSize) * gridSize;
  };

  // Snap Koordinaten zu Grid-Mittelpunkt - verbessert für alle Element-Größen
  const snapToGridCenter = (x, y, elementWidth, elementHeight) => {
    // Berechne die gewünschte Element-Position
    const centerX = x + elementWidth / 2;
    const centerY = y + elementHeight / 2;
    
    // Snap zum nächsten Grid-Punkt
    const snappedCenterX = Math.round(centerX / gridSize) * gridSize;
    const snappedCenterY = Math.round(centerY / gridSize) * gridSize;
    
    // Position zurückrechnen - stelle sicher, dass sie grid-aligned ist
    return { 
      x: Math.round((snappedCenterX - elementWidth / 2) / gridSize) * gridSize, 
      y: Math.round((snappedCenterY - elementHeight / 2) / gridSize) * gridSize
    };
  };

  // Element hinzufügen
  const addElement = useCallback((elementType) => {
    console.log('➕ Adding element:', elementType, 'to station:', station);
    
    const template = elementLibrary[elementType] || customElements.find(ce => ce.type === elementType);
    if (!template) {
      console.error('❌ Template not found for:', elementType);
      return;
    }
    
    // Verwende snapToGridCenter für präzise Startposition
    const snapped = snapToGridCenter(150, 150, template.width, template.height);
    
    const newElement = {
      id: Date.now(),
      type: elementType,
      x: snapped.x,
      y: snapped.y,
      width: template.width,
      height: template.height,
      rotation: 0,
      color: template.color,
      strokeColor: template.strokeColor,
      icon: template.icon,
      elementType: template.type,
      isCustom: !elementLibrary[elementType], // Markiere Custom Elements
      // Doppelte Bauzäune haben zwei separate Seiten
      ...(template.type === 'fence_wood_double' && {
        sideA: { assigned: false, systemId: null, systemNumber: null },
        sideB: { assigned: false, systemId: null, systemNumber: null }
      })
    };
    
    console.log('🎯 New element created:', newElement);
    
    // History snapshot vor der Änderung erstellen
    saveToHistory(`Element "${elementType}" hinzugefügt`);
    
    setElements(prev => {
      const newElements = [...prev, newElement];
      console.log('📦 Elements after add:', newElements);
      
      // Auto-save nach dem Hinzufügen
      setTimeout(async () => {
        console.log('💾 Auto-saving elements to server...');
        try {
          const response = await fetch(`/api/station-layouts/${station}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ elements: newElements, fenceSystems })
          });
          console.log('📡 Save response status:', response.status);
          if (response.ok) {
            console.log('✅ Element erfolgreich gespeichert');
          } else {
            console.error('❌ Save failed:', await response.text());
          }
        } catch (error) {
          console.error('❌ Fehler beim Auto-Save:', error);
        }
      }, 100);
      
      return newElements;
    });
  }, [elementLibrary, customElements, elements, fenceSystems, station]);

  // Element an spezifischer Position hinzufügen (für Drag-and-Drop)
  const addElementAtPosition = (elementType, x, y) => {
    const template = elementLibrary[elementType] || customElements.find(ce => ce.type === elementType);
    if (!template) return;
    
    // Verwende snapToGridCenter für präzise Positionierung an Maus-Position
    const snapped = snapToGridCenter(x, y, template.width, template.height);
    
    const newElement = {
      id: Date.now(),
      type: elementType,
      x: snapped.x,
      y: snapped.y,
      width: template.width,
      height: template.height,
      rotation: 0,
      color: template.color,
      strokeColor: template.strokeColor,
      icon: template.icon,
      elementType: template.type,
      isCustom: !elementLibrary[elementType], // Markiere Custom Elements
      // Doppelte Bauzäune haben zwei separate Seiten
      ...(template.type === 'fence_wood_double' && {
        sideA: { assigned: false, systemId: null, systemNumber: null },
        sideB: { assigned: false, systemId: null, systemNumber: null }
      })
    };
    
    setElements([...elements, newElement]);
  };

  // Custom Element erstellen
  const createCustomElement = async () => {
    if (!newCustomElement.name.trim()) {
      notify('Bitte geben Sie einen Namen für das Element ein.');
      return;
    }

    const customElement = {
      ...newCustomElement,
      type: `custom_${Date.now()}`,
      width: newCustomElement.realWidth * 20, // Convert to pixels
      height: newCustomElement.realHeight * 20,
      realSize: `${newCustomElement.realWidth}x${newCustomElement.realHeight}m`,
      realHeight: newCustomElement.realHeightZ,
      heightInfo: `Custom Element: ${newCustomElement.description || 'Benutzerdefiniert'}`
    };

    try {
      // Auf Server speichern
      const response = await fetch(`/api/station-layouts/${station}/custom-elements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': user?.id || 1
        },
        body: JSON.stringify({ elementData: customElement })
      });

      if (response.ok) {
        setCustomElements([...customElements, customElement]);
        
        // Neues Custom Element automatisch als sichtbar markieren
        setElementVisibility(prev => ({
          ...prev,
          [customElement.type]: true
        }));
        
        console.log('Custom Element erfolgreich auf Server gespeichert');
      } else {
        // Fallback: localStorage
        setCustomElements([...customElements, customElement]);
        localStorage.setItem(`customElements_${station}`, JSON.stringify([...customElements, customElement]));
        
        // Neues Custom Element automatisch als sichtbar markieren
        setElementVisibility(prev => ({
          ...prev,
          [customElement.type]: true
        }));
        
        console.warn('Custom Element im localStorage gespeichert (Server nicht verfügbar)');
      }
    } catch (error) {
      console.error('Fehler beim Speichern des Custom Elements:', error);
      // Fallback: localStorage
      setCustomElements([...customElements, customElement]);
      localStorage.setItem(`customElements_${station}`, JSON.stringify([...customElements, customElement]));
      
      // Neues Custom Element automatisch als sichtbar markieren
      setElementVisibility(prev => ({
        ...prev,
        [customElement.type]: true
      }));
    }
    
    // Form zurücksetzen
    setNewCustomElement({
      name: '',
      icon: '🟦',
      width: 20,
      height: 20,
      color: '#4a90e2',
      strokeColor: '#2c5aa0',
      realWidth: 1.0,
      realHeight: 1.0,
      realHeightZ: 1.0,
      category: 'umgebung',
      description: ''
    });
    
    setShowCustomElementForm(false);
  };

  // Custom Element löschen
  const deleteCustomElement = async (elementType) => {
    try {
      // Vom Server löschen
      const response = await fetch(`/api/station-layouts/${station}/custom-elements/${elementType}`, {
        method: 'DELETE',
        headers: {
          'user-id': user?.id || 1
        }
      });

      if (response.ok) {
        setCustomElements(customElements.filter(ce => ce.type !== elementType));
        console.log('Custom Element erfolgreich vom Server gelöscht');
      } else {
        // Fallback: localStorage
        const updatedElements = customElements.filter(ce => ce.type !== elementType);
        setCustomElements(updatedElements);
        localStorage.setItem(`customElements_${station}`, JSON.stringify(updatedElements));
        console.warn('Custom Element aus localStorage entfernt (Server nicht verfügbar)');
      }
    } catch (error) {
      console.error('Fehler beim Löschen des Custom Elements:', error);
      // Fallback: localStorage
      const updatedElements = customElements.filter(ce => ce.type !== elementType);
      setCustomElements(updatedElements);
      localStorage.setItem(`customElements_${station}`, JSON.stringify(updatedElements));
    }
    
    // Auch aus der Station entfernen
    setElements(elements.filter(el => el.elementType !== elementType));
  };

  // Drag-and-Drop Handler für Element-Bibliothek
  const handleDragStart = (e, elementType) => {
    e.dataTransfer.setData('text/plain', elementType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const elementType = e.dataTransfer.getData('text/plain');
    
    if (elementType) {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left - panOffset.x) / zoomLevel;
      const y = (e.clientY - rect.top - panOffset.y) / zoomLevel;
      
      // Element an Maus-Position hinzufügen
      addElementAtPosition(elementType, x, y);
    }
  };

  // Maus-Events für Selektion und Dragging
  const handleMouseDown = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left - panOffset.x) / zoomLevel;
    const y = (e.clientY - rect.top - panOffset.y) / zoomLevel;
    
    // Debug: Mouse-Koordinaten ausgeben
    console.log('Mouse click:', {
      clientX: e.clientX,
      clientY: e.clientY,
      canvasRect: rect,
      panOffset,
      zoomLevel,
      worldX: x,
      worldY: y
    });
    
    // Maßband-Modus
    if (measureMode && e.button === 0) {
      if (!isMeasuring) {
        // Messung starten
        setMeasureStart({ x, y });
        setMeasureEnd({ x, y });
        setIsMeasuring(true);
        canvas.style.cursor = 'crosshair';
      } else {
        // Messung beenden und speichern
        const distance = Math.sqrt(Math.pow(x - measureStart.x, 2) + Math.pow(y - measureStart.y, 2));
        const realDistance = (distance / 20).toFixed(2); // 20px = 1m im Maßstab
        
        const newMeasurement = {
          id: Date.now(),
          start: measureStart,
          end: { x, y },
          distance: realDistance,
          timestamp: new Date().toLocaleTimeString()
        };
        
        setMeasurements([...measurements, newMeasurement]);
        setIsMeasuring(false);
        setMeasureStart(null);
        setMeasureEnd(null);
        canvas.style.cursor = 'crosshair';
      }
      e.preventDefault();
      return;
    }

    // Bauzaun-System Modus
    if (fenceSystemMode && e.button === 0) {
      console.log('🚧 Fence System Mode - Click detected at:', { x, y });
      console.log('🚧 Total elements:', elements.length);
      
      // Alle Bauzäune zur Debug-Ausgabe
      const allFences = elements.filter(el => 
        el.type?.includes('fence') || 
        el.type === 'fence_foot' || 
        el.type?.includes('bauzaun')
      );
      console.log('🚧 All fence elements:', allFences.map(f => ({ id: f.id, type: f.type, pos: { x: f.x, y: f.y } })));
      
      // Prüfen ob ein Bauzaun geklickt wurde
      const clickedFence = elements.find(element => {
        if (element.type?.includes('fence') || element.type === 'fence_foot' || element.type?.includes('bauzaun')) {
          // Nur sichtbare Elemente für Hit-Detection berücksichtigen
          if (!isElementVisible(element)) {
            return false;
          }
          const hit = isPointInElement(x, y, element);
          console.log(`🚧 Testing fence ${element.id}: ${hit ? 'HIT' : 'miss'}`);
          return hit;
        }
        return false;
      });
      
      console.log('🚧 Final clicked fence:', clickedFence?.id || 'NONE');

      if (clickedFence) {
        // Spezielle Behandlung für doppelte Bauzäune
        if (clickedFence.elementType === 'fence_wood_double') {
          const side = getDoubleFenceSide(x, y, clickedFence);
          console.log(`🚧 Clicked double fence ${clickedFence.id} on side ${side}`);
          
          // Prüfen ob diese Seite bereits ausgewählt ist
          const isAlreadySelected = currentSystemFences.some(fence => 
            fence.id === clickedFence.id && fence.selectedSide === side
          );
          
          if (isAlreadySelected) {
            // Diese Seite entfernen
            setCurrentSystemFences(prev => 
              prev.filter(fence => !(fence.id === clickedFence.id && fence.selectedSide === side))
            );
          } else {
            // Diese Seite hinzufügen
            const fenceWithSide = { ...clickedFence, selectedSide: side };
            setCurrentSystemFences(prev => [...prev, fenceWithSide]);
          }
        } else {
          // Normale Bauzaun-Behandlung
          setCurrentSystemFences(prev => {
            const isAlreadySelected = prev.some(fence => fence.id === clickedFence.id);
            if (isAlreadySelected) {
              // Bauzaun entfernen wenn bereits ausgewählt
              return prev.filter(fence => fence.id !== clickedFence.id);
            } else {
              // Bauzaun hinzufügen
              return [...prev, clickedFence];
            }
          });
        }
        
        setIsDefiningSystem(true);
        e.preventDefault();
        return;
      } else {
        // Klick ins Leere oder auf anderes Element
        if (currentSystemFences.length > 0) {
          // System abschließen wenn Bauzäune ausgewählt sind
          setShowSystemNumberInput(true);
          e.preventDefault();
          return;
        }
        // Fall-through zur normalen Element-Selektion erlauben
      }
    }

    // Rechtsklick für individuelle Bauzaun-Nummer
    if (e.button === 2) { // Rechte Maustaste
      const clickedFence = elements.find(element => {
        if (element.type?.includes('fence') || element.type === 'fence_foot' || element.type?.includes('bauzaun')) {
          // Nur sichtbare Elemente für Hit-Detection berücksichtigen
          if (!isElementVisible(element)) {
            return false;
          }
          return isPointInElement(x, y, element);
        }
        return false;
      });

      if (clickedFence) {
        const fenceNumber = prompt('Nummer für diesen Bauzaun eingeben:', clickedFence.fenceNumber || '');
        if (fenceNumber !== null) {
          setElements(prev => prev.map(element => 
            element.id === clickedFence.id 
              ? { ...element, fenceNumber: fenceNumber.trim() || undefined }
              : element
          ));
        }
        e.preventDefault();
        return;
      }
    }
    
    // Normale Modus-Logik nur wenn nicht im Maßband-Modus oder Bauzaun-System-Modus
    if (!measureMode && !fenceSystemMode) {
      // Prüfen ob ein Element unter der Maus ist (in Z-Order von vorne nach hinten)
      // Sortiere Elements in umgekehrter Z-Order (Vordergrund zuerst) für Hit-Detection
      const sortedElements = [...elements].sort((a, b) => {
        const getZIndex = (element) => {
          if (element.type?.includes('zelt') || element.type === 'partyzelt') return 1;
          if (element.type?.includes('container')) return 2;
          if (element.type?.includes('fence') || element.type === 'fence_foot') return 3;
          if (element.type?.includes('regal') || element.type?.includes('table') || element.type === 'biertisch') return 4;
          return 3;
        };
        return getZIndex(b) - getZIndex(a); // Umgekehrte Reihenfolge für Hit-Detection
      });
      
      const clickedElement = sortedElements.find(element => {
        // Nur sichtbare Elemente für Hit-Detection berücksichtigen
        if (!isElementVisible(element)) {
          return false; // Unsichtbare Elemente überspringen
        }
        
        const hit = isPointInElement(x, y, element);
        // Debug: Zeige alle geprüften Elemente
        if (element.type?.includes('fence') || element.type === 'fence_foot') {
          console.log(`Checking element ${element.id} (${element.type}):`, hit);
        }
        return hit;
      });
      
      // Debug: Zeige finales Ergebnis
      console.log('Final clicked element:', clickedElement?.id, clickedElement?.type);

      // Hauptlogik: Element ausgewählt → Element verschieben, kein Element ausgewählt → Ansicht verschieben
      if (e.button === 0) { // Nur linke Maustaste
        if (selectedElement) {
          // Ein Element ist bereits ausgewählt → immer das ausgewählte Element verschieben
          const dx = x - selectedElement.x;
          const dy = y - selectedElement.y;
          
          const isClickingSelectedElement = isPointInElement(x, y, selectedElement);
          
          if (isClickingSelectedElement) {
            // Klick auf das ausgewählte Element → Element verschieben
            setIsDragging(true);
            setDragOffset({ x: dx, y: dy });
            canvas.style.cursor = 'grabbing';
            e.preventDefault();
            e.stopPropagation();
            return;
          } else {
            // Klick außerhalb des ausgewählten Elements → neues Element auswählen oder Auswahl aufheben
            if (clickedElement) {
              // Neues Element auswählen und sofort dragging starten
              setSelectedElement(clickedElement);
              setIsDragging(true);
              setDragOffset({
                x: x - clickedElement.x,
                y: y - clickedElement.y
              });
              canvas.style.cursor = 'grabbing';
              e.preventDefault();
              e.stopPropagation();
              return;
            } else {
              // Kein Element getroffen → Auswahl aufheben und Ansicht verschieben
              setSelectedElement(null);
              setIsPanning(true);
              setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
              canvas.style.cursor = 'grabbing';
              e.preventDefault();
              return;
            }
          }
        } else {
          // Kein Element ausgewählt
          if (clickedElement) {
            // Element getroffen → Element auswählen und dragging starten
            setSelectedElement(clickedElement);
            setIsDragging(true);
            setDragOffset({
              x: x - clickedElement.x,
              y: y - clickedElement.y
            });
            canvas.style.cursor = 'grabbing';
            e.preventDefault();
            e.stopPropagation();
            return;
          } else {
            // Kein Element getroffen → Ansicht verschieben
            setIsPanning(true);
            setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
            canvas.style.cursor = 'grabbing';
            e.preventDefault();
            return;
          }
        }
      }
      
      // Mittlere Maustaste oder Shift+Linke Maustaste → immer Ansicht verschieben
      if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
        setIsPanning(true);
        setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
        canvas.style.cursor = 'grabbing';
        e.preventDefault();
        return;
      }
    }
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left - panOffset.x) / zoomLevel;
    const y = (e.clientY - rect.top - panOffset.y) / zoomLevel;
    
    // Maßband-Modus: Live-Update der Messlinie
    if (measureMode && isMeasuring) {
      setMeasureEnd({ x, y });
      return;
    }
    
    // Pan-Modus aktiv
    if (isPanning) {
      const newPanOffset = {
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      };
      setPanOffset(newPanOffset);
      return;
    }
    
    // Element-Dragging aktiv - verwende dragPreview für smoothes Rendering
    if (isDragging && selectedElement && !measureMode) {
      // Verwende snapToGridCenter für präzise Positionierung
      const snapped = snapToGridCenter(
        x - dragOffset.x,
        y - dragOffset.y,
        selectedElement.width,
        selectedElement.height
      );
      
      // Throttle drag updates für bessere Performance
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
      
      dragTimeoutRef.current = setTimeout(() => {
        setDragPreview({
          id: selectedElement.id,
          x: snapped.x,
          y: snapped.y
        });
      }, 16); // ~60fps
      return;
    }
    
    // Cursor-Feedback
    if (measureMode) {
      canvas.style.cursor = 'crosshair';
    } else if (fenceSystemMode) {
      // Bauzaun-System Modus: Spezielle Cursor für Bauzäune
      const hoveredFence = elements.find(element => {
        if (element.type?.includes('fence') || element.type === 'fence_foot') {
          // Nur sichtbare Elemente für Hover-Detection berücksichtigen
          if (!isElementVisible(element)) {
            return false;
          }
          return isPointInElement(x, y, element);
        }
        return false;
      });
      
      if (hoveredFence) {
        const isAlreadySelected = currentSystemFences.some(fence => fence.id === hoveredFence.id);
        canvas.style.cursor = isAlreadySelected ? 'not-allowed' : 'pointer';
      } else {
        canvas.style.cursor = currentSystemFences.length > 0 ? 'crosshair' : 'default';
      }
    } else {
      // Normale Cursor-Logik für Elements und Pan
      const hoveredElement = elements.findLast(element => {
        // Nur sichtbare Elemente für Hover-Detection berücksichtigen
        if (!isElementVisible(element)) {
          return false;
        }
        return isPointInElement(x, y, element);
      });
      
      if (hoveredElement) {
        canvas.style.cursor = 'grab';
        setCanPan(false);
      } else if (e.shiftKey) {
        canvas.style.cursor = 'grab';
        setCanPan(true);
      } else {
        canvas.style.cursor = 'grab';
        setCanPan(true);
      }
    }
  };

  const handleMouseUp = () => {
    const canvas = canvasRef.current;
    
    // Finales Update nur einmal beim Drop wenn dragPreview existiert
    if (isDragging && selectedElement && dragPreview) {
      // History snapshot vor der Änderung erstellen
      saveToHistory(`Element "${selectedElement.type}" verschoben`);
      
      setElements(prevElements => prevElements.map(element => 
        element.id === selectedElement.id 
          ? { ...element, x: dragPreview.x, y: dragPreview.y }
          : element
      ));
    }
    
    // Cleanup drag timeout
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
    
    setIsDragging(false);
    setIsPanning(false);
    setDragPreview(null);
    
    // Cursor zurücksetzen
    if (canvas) {
      canvas.style.cursor = 'default';
    }
  };

  // Element löschen
  const deleteSelectedElement = () => {
    if (selectedElement) {
      // History snapshot vor der Änderung erstellen
      saveToHistory(`Element "${selectedElement.type}" gelöscht`);
      
      setElements(elements.filter(el => el.id !== selectedElement.id));
      setSelectedElement(null);
    }
  };

  // Element rotieren
  const rotateSelectedElement = () => {
    if (selectedElement) {
      // Speichere aktuellen Zustand vor Rotation
      saveToHistory(`Element "${selectedElement.type}" rotiert`);
      
      const newRotation = ((selectedElement.rotation || 0) + 90) % 360;
      const updatedElements = elements.map(element => 
        element.id === selectedElement.id 
          ? { ...element, rotation: newRotation }
          : element
      );
      setElements(updatedElements);
      setSelectedElement({ ...selectedElement, rotation: newRotation });
    }
  };

  // Elements-Änderungen an Parent weitergeben
  useEffect(() => {
    if (onElementsChange) {
      onElementsChange(elements);
    }
  }, [elements, onElementsChange]);

  // Fence Systems Änderungen als ungespeichert markieren
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [fenceSystems]);

  // Element-Sichtbarkeit Änderungen als ungespeichert markieren
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [elementVisibility]);

  return (
    <div className="row">
      {/* Station-Info Header */}
      <div className="col-12 mb-3">
        <div className="alert alert-info d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <i className="fas fa-map-marker-alt me-2 fs-4"></i>
            <div>
              <h5 className="mb-0">📍 Aktuelle Station: <strong className="text-primary">{station}</strong></h5>
              <small className="text-muted">
                Alle Änderungen werden für Station {station} gespeichert 
                {isLoadingStation && (
                  <span className="text-info ms-2">
                    <i className="fas fa-spinner fa-spin me-1"></i>
                    Station wird geladen...
                  </span>
                )}
                {hasUnsavedChanges && !isLoadingStation && (
                  <span className="text-warning ms-2">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    Ungespeicherte Änderungen
                  </span>
                )}
              </small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {lastSaved && (
              <small className="text-muted">
                Zuletzt gespeichert: {lastSaved.toLocaleTimeString()}
              </small>
            )}
            {isSaving && (
              <div className="text-primary">
                <i className="fas fa-spinner fa-spin me-1"></i>
                Speichert...
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Hauptbereich mit 3-spaltigem Layout */}
      <div className="col-12">
        <div className="row">
          {/* Element-Bibliothek */}
          <div className="col-md-2">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">🏗️ Element-Bibliothek</h6>
              </div>
              <div className="card-body">

            {/* Element-Bibliothek */}
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0">🚧 Bauzäune</label>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    const allVisible = ['fence_normal', 'fence_wood_single', 'fence_wood_double', 'fence_with_tarp', 'fence_foot']
                      .every(type => isElementTypeVisible(type));
                    ['fence_normal', 'fence_wood_single', 'fence_wood_double', 'fence_with_tarp', 'fence_foot']
                      .forEach(type => {
                        setElementVisibility(prev => ({ ...prev, [type]: !allVisible }));
                      });
                  }}
                  title="Alle Bauzäune ein-/ausblenden"
                >
                  👁️
                </button>
              </div>
              <div className="d-grid gap-1">
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('fence_normal') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('fence_normal')}
                    title={isElementTypeVisible('fence_normal') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('fence_normal') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-warning btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'bauzaun_normal')}
                    onClick={() => addElement('bauzaun_normal')}
                    style={{ opacity: isElementTypeVisible('fence_normal') ? 1 : 0.5 }}
                  >
                    🚧 Standard
                  </button>
                </div>
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('fence_wood_single') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('fence_wood_single')}
                    title={isElementTypeVisible('fence_wood_single') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('fence_wood_single') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-success btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'bauzaun_holz_einfach')}
                    onClick={() => addElement('bauzaun_holz_einfach')}
                    style={{ opacity: isElementTypeVisible('fence_wood_single') ? 1 : 0.5 }}
                  >
                    🪵 Belattet (einfach)
                  </button>
                </div>
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('fence_wood_double') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('fence_wood_double')}
                    title={isElementTypeVisible('fence_wood_double') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('fence_wood_double') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-secondary btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'fence_wood_double')}
                    onClick={() => addElement('fence_wood_double')}
                    style={{ opacity: isElementTypeVisible('fence_wood_double') ? 1 : 0.5 }}
                  >
                    🪵🪵 Belattet (doppelt)
                  </button>
                </div>
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('fence_with_tarp') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('fence_with_tarp')}
                    title={isElementTypeVisible('fence_with_tarp') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('fence_with_tarp') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-info btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'fence_with_tarp')}
                    onClick={() => addElement('fence_with_tarp')}
                    style={{ opacity: isElementTypeVisible('fence_with_tarp') ? 1 : 0.5 }}
                  >
                    🪖 Mit Plane
                  </button>
                </div>
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('fence_foot') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('fence_foot')}
                    title={isElementTypeVisible('fence_foot') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('fence_foot') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-dark btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'fence_foot')}
                    onClick={() => addElement('fence_foot')}
                    style={{ opacity: isElementTypeVisible('fence_foot') ? 1 : 0.5 }}
                  >
                    🦶 Fuß
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0">⛺️ Zelte</label>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    const allVisible = ['pagoda_4x4', 'pagoda_4x6', 'partyzelt']
                      .every(type => isElementTypeVisible(type));
                    ['pagoda_4x4', 'pagoda_4x6', 'partyzelt']
                      .forEach(type => {
                        setElementVisibility(prev => ({ ...prev, [type]: !allVisible }));
                      });
                  }}
                  title="Alle Zelte ein-/ausblenden"
                >
                  👁️
                </button>
              </div>
              <div className="d-grid gap-1">
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('pagoda_4x4') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('pagoda_4x4')}
                    title={isElementTypeVisible('pagoda_4x4') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('pagoda_4x4') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-info btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'pagoda_4x4')}
                    onClick={() => addElement('pagoda_4x4')}
                    style={{ opacity: isElementTypeVisible('pagoda_4x4') ? 1 : 0.5 }}
                  >
                    🏕️ Pagoda 4x4m
                  </button>
                </div>
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('pagoda_4x6') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('pagoda_4x6')}
                    title={isElementTypeVisible('pagoda_4x6') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('pagoda_4x6') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-info btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'pagoda_4x6')}
                    onClick={() => addElement('pagoda_4x6')}
                    style={{ opacity: isElementTypeVisible('pagoda_4x6') ? 1 : 0.5 }}
                  >
                    🏕️ Pagoda 4x6m
                  </button>
                </div>
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('partyzelt') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('partyzelt')}
                    title={isElementTypeVisible('partyzelt') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('partyzelt') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-warning btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'partyzelt')}
                    onClick={() => addElement('partyzelt')}
                    style={{ opacity: isElementTypeVisible('partyzelt') ? 1 : 0.5 }}
                  >
                    🎪 Partyzelt 7x10m
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0">📦 Container</label>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    const allVisible = ['container_20ft', 'container_40ft']
                      .every(type => isElementTypeVisible(type));
                    ['container_20ft', 'container_40ft']
                      .forEach(type => {
                        setElementVisibility(prev => ({ ...prev, [type]: !allVisible }));
                      });
                  }}
                  title="Alle Container ein-/ausblenden"
                >
                  👁️
                </button>
              </div>
              <div className="d-grid gap-1">
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('container_20ft') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('container_20ft')}
                    title={isElementTypeVisible('container_20ft') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('container_20ft') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-primary btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'container_20ft')}
                    onClick={() => addElement('container_20ft')}
                    style={{ opacity: isElementTypeVisible('container_20ft') ? 1 : 0.5 }}
                  >
                    📦 Container 20ft
                  </button>
                </div>
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('container_40ft') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('container_40ft')}
                    title={isElementTypeVisible('container_40ft') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('container_40ft') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-primary btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'container_40ft')}
                    onClick={() => addElement('container_40ft')}
                    style={{ opacity: isElementTypeVisible('container_40ft') ? 1 : 0.5 }}
                  >
                    📦 Container 40ft
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0">🏪 Möbel & Ausstattung</label>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    const allVisible = ['regal', 'biertisch']
                      .every(type => isElementTypeVisible(type));
                    ['regal', 'biertisch']
                      .forEach(type => {
                        setElementVisibility(prev => ({ ...prev, [type]: !allVisible }));
                      });
                  }}
                  title="Alle Möbel ein-/ausblenden"
                >
                  👁️
                </button>
              </div>
              <div className="d-grid gap-1">
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('regal') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('regal')}
                    title={isElementTypeVisible('regal') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('regal') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-secondary btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'regal')}
                    onClick={() => addElement('regal')}
                    style={{ opacity: isElementTypeVisible('regal') ? 1 : 0.5 }}
                  >
                    📚 Metall-Regal
                  </button>
                </div>
                <div className="d-flex gap-1">
                  <button
                    className={`btn btn-sm ${isElementTypeVisible('biertisch') ? 'text-success' : 'text-muted'}`}
                    onClick={() => toggleElementVisibility('biertisch')}
                    title={isElementTypeVisible('biertisch') ? 'Ausblenden' : 'Einblenden'}
                    style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                  >
                    {isElementTypeVisible('biertisch') ? '👁️' : '🙈'}
                  </button>
                  <button 
                    className="btn btn-outline-warning btn-sm flex-grow-1"
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'biertisch')}
                    onClick={() => addElement('biertisch')}
                    style={{ opacity: isElementTypeVisible('biertisch') ? 1 : 0.5 }}
                  >
                    🪑 Biertisch
                  </button>
                </div>
              </div>
            </div>

            {/* Custom Elements */}
            <div className="mb-3">
              <label className="form-label">🎨 Individuelle Umgebung</label>
              <div className="d-grid gap-1">
                <button 
                  className={`btn btn-sm ${showCustomElementForm ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setShowCustomElementForm(!showCustomElementForm)}
                >
                  ➕ Neues Element erstellen
                </button>
                
                {/* Custom Elements Liste */}
                {customElements.map(element => (
                  <div key={element.type} className="d-flex gap-1">
                    <button
                      className={`btn btn-sm ${isElementTypeVisible(element.type) ? 'text-success' : 'text-muted'}`}
                      onClick={() => toggleElementVisibility(element.type)}
                      title={isElementTypeVisible(element.type) ? 'Ausblenden' : 'Einblenden'}
                      style={{ fontSize: '0.75rem', padding: '2px 4px', minWidth: '24px' }}
                    >
                      {isElementTypeVisible(element.type) ? '👁️' : '🙈'}
                    </button>
                    <button 
                      className="btn btn-outline-primary btn-sm flex-grow-1"
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, element.type)}
                      onClick={() => addElement(element.type)}
                      title={element.description}
                      style={{ opacity: isElementTypeVisible(element.type) ? 1 : 0.5 }}
                    >
                      {element.icon} {element.name}
                    </button>
                    <button 
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => deleteCustomElement(element.type)}
                      title="Löschen"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Custom Element Form */}
              {showCustomElementForm && (
                <div className="mt-2 p-2 border rounded bg-light">
                  <small>
                    <div className="mb-2">
                      <label className="form-label">Name:</label>
                      <input 
                        type="text" 
                        className="form-control form-control-sm"
                        value={newCustomElement.name}
                        onChange={(e) => setNewCustomElement({...newCustomElement, name: e.target.value})}
                        placeholder="z.B. Baum, Parkbank"
                      />
                    </div>
                    
                    <div className="row mb-2">
                      <div className="col-6">
                        <label className="form-label">Breite (m):</label>
                        <input 
                          type="number" 
                          step="0.1"
                          className="form-control form-control-sm"
                          value={newCustomElement.realWidth}
                          onChange={(e) => setNewCustomElement({...newCustomElement, realWidth: parseFloat(e.target.value) || 1})}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Tiefe (m):</label>
                        <input 
                          type="number" 
                          step="0.1"
                          className="form-control form-control-sm"
                          value={newCustomElement.realHeight}
                          onChange={(e) => setNewCustomElement({...newCustomElement, realHeight: parseFloat(e.target.value) || 1})}
                        />
                      </div>
                    </div>
                    
                    <div className="row mb-2">
                      <div className="col-6">
                        <label className="form-label">Höhe 3D (m):</label>
                        <input 
                          type="number" 
                          step="0.1"
                          className="form-control form-control-sm"
                          value={newCustomElement.realHeightZ}
                          onChange={(e) => setNewCustomElement({...newCustomElement, realHeightZ: parseFloat(e.target.value) || 1})}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Icon:</label>
                        <select 
                          className="form-control form-control-sm"
                          value={newCustomElement.icon}
                          onChange={(e) => setNewCustomElement({...newCustomElement, icon: e.target.value})}
                        >
                          <option value="🌳">🌳 Baum</option>
                          <option value="🪑">🪑 Parkbank</option>
                          <option value="🚗">🚗 Fahrzeug</option>
                          <option value="🚲">🚲 Fahrrad</option>
                          <option value="🗑️">🗑️ Mülleimer</option>
                          <option value="💡">💡 Laterne</option>
                          <option value="🚧">🚧 Hindernis</option>
                          <option value="🏢">🏢 Gebäude</option>
                          <option value="🟦">🟦 Objekt</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="row mb-2">
                      <div className="col-6">
                        <label className="form-label">Farbe:</label>
                        <input 
                          type="color" 
                          className="form-control form-control-sm"
                          value={newCustomElement.color}
                          onChange={(e) => setNewCustomElement({...newCustomElement, color: e.target.value})}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Rand:</label>
                        <input 
                          type="color" 
                          className="form-control form-control-sm"
                          value={newCustomElement.strokeColor}
                          onChange={(e) => setNewCustomElement({...newCustomElement, strokeColor: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <label className="form-label">Beschreibung (optional):</label>
                      <input 
                        type="text" 
                        className="form-control form-control-sm"
                        value={newCustomElement.description}
                        onChange={(e) => setNewCustomElement({...newCustomElement, description: e.target.value})}
                        placeholder="Zusätzliche Informationen"
                      />
                    </div>
                    
                    <div className="d-grid gap-1">
                      <button 
                        className="btn btn-success btn-sm"
                        onClick={createCustomElement}
                      >
                        ✅ Element erstellen
                      </button>
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowCustomElementForm(false)}
                      >
                        ❌ Abbrechen
                      </button>
                    </div>
                  </small>
                </div>
              )}
            </div>

            {/* Element-Kontrollen */}
            {selectedElement && (
              <div className="mb-3">
                <label className="form-label">Ausgewähltes Element</label>
                <div className="d-grid gap-1">
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={rotateSelectedElement}
                  >
                    <i className="bi bi-arrow-clockwise"></i> Drehen
                  </button>
                  <button 
                    className="btn btn-outline-danger btn-sm"
                    onClick={deleteSelectedElement}
                  >
                    <i className="bi bi-trash"></i> Löschen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Canvas-Bereich */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="d-flex gap-3 align-items-center flex-wrap">
              {/* Zoom-Kontrollen */}
              <div className="d-flex align-items-center gap-1">
                <small className="text-muted me-2">🔍 Zoom:</small>
                <div className="btn-group">
                  <button className="btn btn-outline-primary btn-sm" onClick={handleZoomIn}>
                    <i className="bi bi-zoom-in"></i>
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={handleZoomReset}>
                    {Math.round(zoomLevel * 100)}%
                  </button>
                  <button className="btn btn-outline-primary btn-sm" onClick={handleZoomOut}>
                    <i className="bi bi-zoom-out"></i>
                  </button>
                </div>
                <button 
                  className="btn btn-outline-info btn-sm" 
                  onClick={() => setPanOffset({ x: 0, y: 0 })}
                  title="Ansicht zentrieren"
                >
                  <i className="bi bi-house"></i>
                </button>
              </div>

              {/* Speichern Button */}
              <div className="d-flex align-items-center gap-1">
                <button 
                  className={`btn btn-sm ${hasUnsavedChanges ? 'btn-danger' : 'btn-outline-success'}`}
                  onClick={saveToServer}
                  disabled={isSaving || elements.length === 0}
                  title={hasUnsavedChanges ? 'Ungespeicherte Änderungen - Jetzt speichern!' : 'Layout ist gespeichert'}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                      Speichert...
                    </>
                  ) : (
                    <>
                      <i className={`bi ${hasUnsavedChanges ? 'bi-exclamation-triangle' : 'bi-check-circle'}`}></i>
                      {hasUnsavedChanges ? ' Speichern' : ' Gespeichert'}
                    </>
                  )}
                </button>
              </div>

              {/* Maßband-Tool */}
              <div className="d-flex align-items-center gap-1">
                <button 
                  className={`btn btn-sm ${measureMode ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => {
                    setMeasureMode(!measureMode);
                    setIsMeasuring(false);
                    setMeasureStart(null);
                    setMeasureEnd(null);
                  }}
                  title={measureMode ? 'Maßband deaktivieren' : 'Maßband aktivieren'}
                >
                  📏 {measureMode ? 'Aktiv' : 'Messen'}
                </button>
                {measurements.length > 0 && (
                  <button 
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => setMeasurements([])}
                    title="Alle Messungen löschen"
                  >
                    🗑️ ({measurements.length})
                  </button>
                )}
              </div>

              {/* Bauzaun-System Tool */}
              <div className="d-flex align-items-center gap-1">
                <button 
                  className={`btn btn-sm ${fenceSystemMode ? 'btn-info' : 'btn-outline-info'}`}
                  onClick={() => {
                    setFenceSystemMode(!fenceSystemMode);
                    setIsDefiningSystem(false);
                    setCurrentSystemFences([]);
                    setShowSystemNumberInput(false);
                    setSystemNumberInput('');
                    // Deaktiviere Maßband wenn Bauzaun-System aktiviert wird
                    if (!fenceSystemMode) {
                      setMeasureMode(false);
                      setIsMeasuring(false);
                      setMeasureStart(null);
                      setMeasureEnd(null);
                    }
                  }}
                  title={fenceSystemMode ? 'Bauzaun-System deaktivieren' : 'Bauzaun-System aktivieren'}
                >
                  🚧 {fenceSystemMode ? 'Aktiv' : 'System'}
                </button>
                {fenceSystems.length > 0 && (
                  <button 
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => setFenceSystems([])}
                    title="Alle Bauzaun-Systeme löschen"
                  >
                    🗑️ ({fenceSystems.length})
                  </button>
                )}
              </div>

              {/* Anzeige-Optionen */}
              <div className="d-flex align-items-center gap-2">
                <div className="form-check form-check-inline">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="showGrid"
                    checked={showGrid}
                    onChange={(e) => setShowGrid(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="showGrid">
                    <small>Raster</small>
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="showIcons"
                    checked={showIcons}
                    onChange={(e) => setShowIcons(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="showIcons">
                    <small>Icons</small>
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="showMeasurements"
                    checked={showMeasurements}
                    onChange={(e) => setShowMeasurements(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="showMeasurements">
                    <small>Maßstab</small>
                  </label>
                </div>
                {fenceSystemMode && (
                  <div className="form-check form-check-inline">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="showHitAreas"
                      checked={showHitAreas}
                      onChange={(e) => setShowHitAreas(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showHitAreas">
                      <small style={{color: '#dc3545'}}>Hit-Areas</small>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div>
              {measureMode && (
                <span className="badge bg-warning me-2">
                  📏 Maßband aktiv - Klicken zum Messen
                </span>
              )}
              {fenceSystemMode && !isDefiningSystem && (
                <span className="badge bg-info me-2">
                  🚧 Bauzaun-System - Mehrere Bauzäune anklicken zum Gruppieren
                </span>
              )}
              {fenceSystemMode && isDefiningSystem && (
                <span className="badge bg-info me-2">
                  🚧 System definieren ({currentSystemFences.length} Bauzäune) - Ins Leere klicken zum Abschließen
                </span>
              )}
              {!measureMode && !fenceSystemMode && (
                <span className="badge bg-secondary me-2">
                  💡 Rechtsklick auf Bauzaun für individuelle Nummer
                </span>
              )}
              {hasUnsavedChanges && !isSaving && (
                <span className="badge bg-danger me-2">⚠️ Ungespeicherte Änderungen</span>
              )}
              {isSaving && <span className="badge bg-warning me-2">💾 Speichert...</span>}
              {lastSaved && (
                <small className="text-muted">
                  Zuletzt gespeichert: {lastSaved.toLocaleTimeString()}
                </small>
              )}
            </div>
          </div>
          <div className="card-body p-0">
            <canvas
              ref={canvasRef}
              style={{ 
                border: '1px solid #ddd', 
                cursor: isDragging || isPanning ? 'grabbing' : 'grab',
                userSelect: 'none'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp} // Behandle Maus verlässt Canvas
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onContextMenu={(e) => e.preventDefault()} // Verhindere Context-Menü
            />
          </div>
        </div>
      </div>

      {/* Bauzaun-Systeme - Rechts vom Canvas */}
      <div className="col-md-2">
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">🚧 Bauzaun-Systeme</h6>
            <small className="text-muted">{fenceSystems.length} System{fenceSystems.length !== 1 ? 'e' : ''}</small>
          </div>
          <div className="card-body">
            {fenceSystems.length === 0 ? (
              <div className="text-center text-muted">
                <small>
                  <div>🏗️</div>
                  Noch keine Systeme erstellt
                </small>
              </div>
            ) : (
              <div className="d-grid gap-2">
                {fenceSystems.map((system, index) => (
                  <div 
                    key={index}
                    className={`border rounded p-2 cursor-pointer ${
                      selectedSystemId === index ? 'border-primary bg-light' : 'border-secondary'
                    }`}
                    onClick={() => {
                      setSelectedSystemId(selectedSystemId === index ? null : index);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong className="text-primary">#{system.number}</strong>
                        <div>
                          <small className="text-muted">
                            {system.fences.length} Zaun{system.fences.length !== 1 ? 'e' : ''}
                          </small>
                        </div>
                        <div>
                          <small className="text-muted">
                            {system.timestamp}
                          </small>
                        </div>
                      </div>
                      <div className="dropdown">
                        <button 
                          className="btn btn-outline-secondary btn-sm dropdown-toggle"
                          type="button"
                          data-bs-toggle="dropdown"
                          onClick={(e) => e.stopPropagation()}
                        >
                          ⚙️
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <button 
                              className="dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingSystemId(index);
                              }}
                            >
                              ✏️ Bearbeiten
                            </button>
                          </li>
                          <li>
                            <button 
                              className="dropdown-item text-danger"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (await confirmDialog(`System "${system.number}" wirklich löschen?`)) {
                                  // Speichere aktuellen Zustand vor System-Löschung
                                  saveToHistory(`Bauzaun-System "${system.number}" gelöscht`);
                                  
                                  setFenceSystems(prev => prev.filter((_, i) => i !== index));
                                  if (selectedSystemId === index) {
                                    setSelectedSystemId(null);
                                  }
                                }
                              }}
                            >
                              🗑️ Löschen
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>

  {/* Untere Reihe: Statistik und Aufbauanleitung */}
  <div className="col-12 mt-4">
    <div className="row">
      {/* Statistik - Links unten (schmal) */}
      <div className="col-md-3">
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">📊 Statistik</h6>
            <small className="text-muted">Station: {currentStation}</small>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                {/* Bauzäune - nur anzeigen wenn vorhanden */}
                {(() => {
                  // Filter nach type und elementType für bessere Kompatibilität
                  const bauzaunElements = elements.filter(el => 
                    el.type?.includes('bauzaun') || 
                    el.type?.includes('fence') || 
                    el.elementType?.includes('fence')
                  );
                  const bauzaunCount = bauzaunElements.length;
                  
                  const standardBauzaun = elements.filter(el => 
                    el.type === 'bauzaun' || 
                    el.type === 'bauzaun_normal' || 
                    el.elementType === 'fence_normal'
                  ).length;
                  
                  const fussBauzaun = elements.filter(el => 
                    el.type === 'fence_foot' || 
                    el.elementType === 'fence_foot'
                  ).length;
                  
                  const doppelBauzaun = elements.filter(el => 
                    el.type === 'bauzaun_doppel' || 
                    el.type === 'bauzaun_holz_doppel' ||
                    el.type === 'fence_wood_double' || 
                    el.elementType === 'fence_wood_double'
                  ).length;
                  
                  const holzEinfach = elements.filter(el => 
                    el.type === 'bauzaun_holz_einfach' || 
                    el.elementType === 'fence_wood_single'
                  ).length;
                  
                  const mitPlane = elements.filter(el => 
                    el.type === 'fence_with_tarp' || 
                    el.elementType === 'fence_with_tarp'
                  ).length;
                  
                  if (bauzaunCount === 0) return null;
                  
                  return (
                    <div className="mb-2">
                      <div className="d-flex align-items-center mb-1">
                        <span className="badge bg-primary me-2">🚧</span>
                        <small className="text-muted"><strong>Bauzäune ({bauzaunCount}):</strong></small>
                      </div>
                      <div className="ms-3">
                        <small className="text-muted">
                          {standardBauzaun > 0 && `• Standard: ${standardBauzaun}x`}<br/>
                          {fussBauzaun > 0 && `• Füße: ${fussBauzaun}x`}<br/>
                          {doppelBauzaun > 0 && `• Doppel: ${doppelBauzaun}x`}<br/>
                          {holzEinfach > 0 && `• Holz einfach: ${holzEinfach}x`}<br/>
                          {mitPlane > 0 && `• Mit Plane: ${mitPlane}x`}
                        </small>
                      </div>
                    </div>
                  );
                })()}

                {/* Container - nur anzeigen wenn vorhanden */}
                {(() => {
                  const containerElements = elements.filter(el => 
                    el.type?.includes('container') || 
                    el.elementType?.includes('container')
                  );
                  const containerCount = containerElements.length;
                  
                  const container20ft = elements.filter(el => 
                    el.type === 'container_20ft' || 
                    el.elementType === 'container_20ft'
                  ).length;
                  
                  const container40ft = elements.filter(el => 
                    el.type === 'container_40ft' || 
                    el.elementType === 'container_40ft'
                  ).length;
                  
                  if (containerCount === 0) return null;
                  
                  return (
                    <div className="mb-2">
                      <div className="d-flex align-items-center mb-1">
                        <span className="badge bg-success me-2">📦</span>
                        <small className="text-muted"><strong>Container ({containerCount}):</strong></small>
                      </div>
                      <div className="ms-3">
                        <small className="text-muted">
                          {container20ft > 0 && `• 20ft: ${container20ft}x`}<br/>
                          {container40ft > 0 && `• 40ft: ${container40ft}x`}
                        </small>
                      </div>
                    </div>
                  );
                })()}

                {/* Zelte - nur anzeigen wenn vorhanden */}
                {(() => {
                  const zelteElements = elements.filter(el => 
                    el.type?.includes('zelt') || 
                    el.elementType?.includes('pagoda') || 
                    el.elementType === 'partyzelt'
                  );
                  const zelteCount = zelteElements.length;
                  
                  const pagoda4x4 = elements.filter(el => 
                    el.type === 'pagoda_4x4' || 
                    el.type === 'pagodenzelt_4x4' || 
                    el.elementType === 'pagoda_4x4'
                  ).length;
                  
                  const pagoda4x6 = elements.filter(el => 
                    el.type === 'pagoda_4x6' || 
                    el.type === 'pagodenzelt_4x6' || 
                    el.elementType === 'pagoda_4x6'
                  ).length;
                  
                  const partyzelt = elements.filter(el => 
                    el.type === 'partyzelt' || 
                    el.elementType === 'partyzelt'
                  ).length;
                  
                  if (zelteCount === 0) return null;
                  
                  return (
                    <div className="mb-2">
                      <div className="d-flex align-items-center mb-1">
                        <span className="badge bg-warning me-2">⛺</span>
                        <small className="text-muted"><strong>Zelte ({zelteCount}):</strong></small>
                      </div>
                      <div className="ms-3">
                        <small className="text-muted">
                          {pagoda4x4 > 0 && `• Pagoda 4x4m: ${pagoda4x4}x`}<br/>
                          {pagoda4x6 > 0 && `• Pagoda 4x6m: ${pagoda4x6}x`}<br/>
                          {partyzelt > 0 && `• Partyzelt: ${partyzelt}x`}
                        </small>
                      </div>
                    </div>
                  );
                })()}

                {/* Möbel - nur anzeigen wenn vorhanden */}
                {(() => {
                  const regale = elements.filter(el => 
                    el.type === 'regal' || 
                    el.elementType === 'regal'
                  ).length;
                  
                  const biertische = elements.filter(el => 
                    el.type === 'biertisch' || 
                    el.elementType === 'biertisch'
                  ).length;
                  
                  const moebel = elements.filter(el => 
                    el.type?.includes('mobel') || 
                    el.elementType?.includes('mobel')
                  ).length;
                  
                  if (regale === 0 && biertische === 0 && moebel === 0) return null;
                  
                  return (
                    <div className="mb-2">
                      <div className="d-flex align-items-center mb-1">
                        <span className="badge bg-info me-2">🪑</span>
                        <small className="text-muted"><strong>Möbel & Ausstattung:</strong></small>
                      </div>
                      <div className="ms-3">
                        <small className="text-muted">
                          {regale > 0 && `• Regale: ${regale}x`}<br/>
                          {biertische > 0 && `• Biertische: ${biertische}x`}<br/>
                          {moebel > 0 && `• Möbel: ${moebel}x`}
                        </small>
                      </div>
                    </div>
                  );
                })()}

                {/* Custom Elemente - nur anzeigen wenn vorhanden */}
                {(() => {
                  const customElements = elements.filter(el => 
                    el.isCustom || 
                    el.type === 'custom' || 
                    el.elementType === 'custom'
                  ).length;
                  
                  if (customElements === 0) return null;
                  
                  return (
                    <div className="mb-2">
                      <div className="d-flex align-items-center mb-1">
                        <span className="badge bg-secondary me-2">🎨</span>
                        <small className="text-muted"><strong>Custom Elemente ({customElements}):</strong></small>
                      </div>
                    </div>
                  );
                })()}

                {/* Gesamt - immer anzeigen */}
                <div className="mt-2 pt-2 border-top">
                  <small className="text-muted">
                    <strong>📏 Gesamt: {elements.length} Elemente</strong>
                    {measurements.length > 0 && (
                      <><br/>📐 Messungen: {measurements.length}x</>
                    )}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Aufbauanleitung - Rechts unten (sehr breit) */}
      <div className="col-md-9">
        <StationBuildGuide
          stationId={currentStation}
          onSave={handleBuildGuideUpdate}
          onDataChange={handleBuildGuideDataChange}
        />
      </div>
    </div>
  </div>

  {/* System-Nummer Input Modal */}
      {showSystemNumberInput && (
        <div className="modal d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🚧 Bauzaun-System erstellen</h5>
              </div>
              <div className="modal-body">
                <p><small>Ausgewählte Bauzäune: {currentSystemFences.length}</small></p>
                
                {/* Zeige ausgewählte Bauzäune mit Seiten-Info */}
                <div className="mb-3">
                  <label className="form-label">Ausgewählte Elemente:</label>
                  <div className="border rounded p-2 bg-light" style={{maxHeight: '120px', overflowY: 'auto'}}>
                    {currentSystemFences.map((fence, index) => (
                      <div key={`${fence.id}-${fence.selectedSide || 'all'}`} className="text-muted" style={{fontSize: '0.85rem'}}>
                        {index + 1}. {fence.elementType} 
                        {fence.selectedSide && (
                          <span className="badge bg-info ms-1">
                            Seite {fence.selectedSide.slice(-1)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">System-Nummer (z.B. "1-233"):</label>
                  <input
                    type="text"
                    className="form-control"
                    value={systemNumberInput}
                    onChange={(e) => setSystemNumberInput(e.target.value)}
                    placeholder="1-233"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // System erstellen
                        if (systemNumberInput.trim()) {
                          // Speichere aktuellen Zustand vor System-Erstellung
                          saveToHistory(`Bauzaun-System "${systemNumberInput.trim()}" erstellt`);
                          
                          const newSystem = {
                            id: Date.now(),
                            number: systemNumberInput.trim(),
                            fences: [...currentSystemFences],
                            timestamp: new Date().toLocaleTimeString()
                          };
                          setFenceSystems(prev => [...prev, newSystem]);
                          
                          // Reset
                          setCurrentSystemFences([]);
                          setIsDefiningSystem(false);
                          setShowSystemNumberInput(false);
                          setSystemNumberInput('');
                        }
                      } else if (e.key === 'Escape') {
                        // Abbrechen
                        setShowSystemNumberInput(false);
                        setSystemNumberInput('');
                      }
                    }}
                  />
                  <div className="form-text">Enter zum Erstellen, Esc zum Abbrechen</div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowSystemNumberInput(false);
                    setSystemNumberInput('');
                  }}
                >
                  Abbrechen
                </button>
                <button 
                  className="btn btn-info btn-sm"
                  onClick={() => {
                    if (systemNumberInput.trim()) {
                      // Speichere aktuellen Zustand vor System-Erstellung
                      saveToHistory(`Bauzaun-System "${systemNumberInput.trim()}" erstellt`);
                      
                      const newSystem = {
                        id: Date.now(),
                        number: systemNumberInput.trim(),
                        fences: [...currentSystemFences],
                        timestamp: new Date().toLocaleTimeString()
                      };
                      setFenceSystems(prev => [...prev, newSystem]);
                      
                      // Reset
                      setCurrentSystemFences([]);
                      setIsDefiningSystem(false);
                      setShowSystemNumberInput(false);
                      setSystemNumberInput('');
                    }
                  }}
                  disabled={!systemNumberInput.trim()}
                >
                  System erstellen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System-Bearbeitung Modal */}
      {editingSystemId !== null && fenceSystems[editingSystemId] && (
        <div className="modal d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🚧 System bearbeiten</h5>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">System-Nummer:</label>
                  <input
                    type="text"
                    className="form-control"
                    value={fenceSystems[editingSystemId]?.number || ''}
                    onChange={(e) => {
                      const newNumber = e.target.value;
                      setFenceSystems(prev => 
                        prev.map((system, index) => 
                          index === editingSystemId 
                            ? { ...system, number: newNumber }
                            : system
                        )
                      );
                    }}
                    placeholder="1-233"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Zugewiesene Zäune ({fenceSystems[editingSystemId]?.fences.length || 0}):</label>
                  <div className="border rounded p-2 bg-light" style={{maxHeight: '200px', overflowY: 'auto'}}>
                    {fenceSystems[editingSystemId]?.fences.map((fence, fenceIndex) => (
                      <div key={fenceIndex} className="d-flex justify-content-between align-items-center border-bottom py-1">
                        <small>
                          <strong>{fence.type}</strong> @ ({fence.x}, {fence.y})
                          {fence.fenceNumber && <span className="badge bg-info ms-1">#{fence.fenceNumber}</span>}
                        </small>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => {
                            setFenceSystems(prev => 
                              prev.map((system, index) => 
                                index === editingSystemId 
                                  ? { 
                                      ...system, 
                                      fences: system.fences.filter((_, i) => i !== fenceIndex)
                                    }
                                  : system
                              )
                            );
                          }}
                        >
                          ❌
                        </button>
                      </div>
                    )) || <small className="text-muted">Keine Zäune zugewiesen</small>}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => setEditingSystemId(null)}
                >
                  Schließen
                </button>
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={async () => {
                    if (await confirmDialog(`System "${fenceSystems[editingSystemId]?.number}" wirklich löschen?`)) {
                      // Speichere aktuellen Zustand vor System-Löschung
                      saveToHistory(`Bauzaun-System "${fenceSystems[editingSystemId]?.number}" gelöscht`);
                      
                      setFenceSystems(prev => prev.filter((_, i) => i !== editingSystemId));
                      if (selectedSystemId === editingSystemId) {
                        setSelectedSystemId(null);
                      }
                      setEditingSystemId(null);
                    }
                  }}
                >
                  🗑️ System löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Undo/Redo Panel */}
      {undoHistory.length > 0 && (
        <div className="position-fixed" style={{
          bottom: '20px',
          right: '20px',
          zIndex: 1060,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxWidth: '300px',
          minWidth: '250px'
        }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="mb-0 text-muted">
              <i className="fas fa-undo me-1"></i>
              Letzte Änderungen
            </h6>
            <small className="text-muted">{undoHistory.length}/3</small>
          </div>
          
          <div className="d-flex flex-column gap-1">
            {undoHistory.map((snapshot, index) => (
              <div key={snapshot.timestamp.getTime()} className="d-flex align-items-center justify-content-between p-2 border rounded bg-light">
                <div className="flex-grow-1 me-2">
                  <div className="fw-bold text-dark" style={{fontSize: '0.875rem'}}>
                    {snapshot.description}
                  </div>
                  <div className="text-muted" style={{fontSize: '0.75rem'}}>
                    {snapshot.timestamp.toLocaleTimeString()} 
                    <span className="ms-1">({index === 0 ? 'zuletzt' : `vor ${index + 1}`})</span>
                  </div>
                </div>
                <button
                  className="btn btn-outline-primary btn-sm"
                  style={{fontSize: '0.75rem', padding: '4px 8px'}}
                  onClick={() => undoLastAction(index)}
                  title={`Rückgängig: ${snapshot.description}`}
                >
                  <i className="fas fa-undo me-1"></i>
                  Rückgängig
                </button>
              </div>
            ))}
          </div>
          
          <div className="mt-2 pt-2 border-top">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Klicken Sie auf "Rückgängig" um Änderungen zu widerrufen
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default StationPlanner2D;